/**
 * ============================================================================
 * PRODUCT CONFIGURATOR
 * ============================================================================
 *
 * ARCHITECTURE OVERVIEW
 * ---------------------
 * This store has two kinds of products:
 *
 *   1. MAIN PRODUCT  — Shown on the storefront (listing + PDP).
 *                      Acts as a "configurator shell". Has option accordions
 *                      (e.g. Backplate → Function → Finish) that the user picks.
 *                      Not actually purchased directly; we resolve it to a SKU.
 *
 *   2. SKU PRODUCT   — Hidden products (product_type: "SKU Product"), one per
 *                      valid combination of options. Carries real price, SKU,
 *                      images, metafields, availability. These ARE what gets
 *                      added to cart.
 *
 * DATA FLOW
 * ---------
 *   User lands on MAIN PRODUCT PDP
 *     → First option selection fires fetchSkuCandidates()
 *         (fetches ALL matching SKU products once, narrowed by ProductCode +
 *          first-option value as tags — avoids loading thousands of SKUs)
 *     → Subsequent option selections filter the in-memory candidate list
 *         (no additional network calls until the full combo is chosen)
 *     → When every option is selected, we build a handle = ProductCode + option
 *       codes and fetchResolvedSkuProduct(handle) to get the live SKU product
 *     → UI (price, images, description, add-to-cart id, etc.) swaps to the SKU
 *
 * OPTION METAFIELD KEYS (on SKU products, used for filtering)
 * -----------------------------------------------------------
 *   rosset_code, finish_code, function_code, by_type_code, timber_code,
 *   type_code, length_code, handing_code, backplate_shape_code
 *
 * EXTERNAL DEPENDENCIES
 * ---------------------
 *   - window.country_code         (set by Liquid, drives @inContext)
 *   - window.Shopify.routes.root  (locale-aware root path)
 *   - galleryMain / galleryThumbs (Swiper instances from PDP template)
 *   - bootstrap.Tooltip           (tooltip re-init after content swap)
 *   - _swat / window.SwymCallbacks (Swym wishlist SDK — "Specbook" feature)
 *
 * ============================================================================
 */


/* ============================================================================
 * 1. CONFIG
 * ========================================================================= */

const Config = {
  SHOPIFY_STORE: "designer-doorware-au.myshopify.com",
  API_VERSION: "2025-01",
  STOREFRONT_ACCESS_TOKEN: "fe897ac638c3b4bbece4814d25e20187",
  SPECBOOK_WORKER_URL: `${window.pdf_url}`,
  SHOPIFY_SKU_PRODUCT_TYPE: "SKU Product",
  PLACEHOLDER_IMAGE_URL: "//www.designerdoorware.com/cdn/shop/files/Placeholder_Image_V2_2026.webp?v=1792595753&width=600",

  // How many SKU products to pull per page when hunting candidates.
  SKU_FETCH_PAGE_SIZE: 250,

  // Delay between pre-populating variants from URL params (lets each change
  // settle + its fetch complete before the next kicks off).
  URL_PARAM_PREFILL_DELAY_MS: 1500,

  // Delay after main Add-to-Cart before firing the optional dropdown product
  // add (e.g. a suggested bolt / backplate add-on).
  DROPDOWN_ADDON_ADD_DELAY_MS: 1000,

  // If background pagination of SKU candidates is still running after this
  // threshold, reveal a tiny loader so the user knows something's still cooking.
  // Below this threshold, background work stays invisible.
  BACKGROUND_LOADER_THRESHOLD_MS: 2000,
};

// Shorthand for Storefront GraphQL endpoint.
const STOREFRONT_URL = `https://${Config.SHOPIFY_STORE}/api/${Config.API_VERSION}/graphql.json`;


/* ============================================================================
 * 2. GRAPHQL QUERIES
 * ----------------------------------------------------------------------------
 * @inContext(country: ...) is interpolated from window.country_code so prices
 * and availability reflect the visitor's market.
 * ========================================================================= */

const Queries = {
  /**
   * Fetch SKU product CANDIDATES for filtering.
   * Keyed by: product_type = "SKU Product" AND tag = ProductCode
   *           AND tag = first-option-value.
   *
   * Pulls ONLY the metafields we need to filter by option combination.
   * `product_media` is intentionally NOT fetched here — the gallery uses the
   * separate Queries.productGalleryById call on demand, which keeps each
   * candidate's payload small (matters a lot when listing 1000+ SKUs).
   */
  skuCandidates: `
    query getProducts($first: Int, $query: String, $after: String) @inContext(country: ${country_code}) {
      products(first: $first, query: $query, after: $after) {
        edges {
          node {
            id
            handle
            title
            tags
            availableForSale
            metafields(identifiers: [
              {namespace: "custom", key: "rosset_code"},
              {namespace: "custom", key: "finish_code"},
              {namespace: "custom", key: "function_code"},
              {namespace: "custom", key: "by_type_code"},
              {namespace: "custom", key: "timber_code"},
              {namespace: "custom", key: "type_code"},
              {namespace: "custom", key: "length_code"},
              {namespace: "custom", key: "handing_code"},
              {namespace: "custom", key: "backplate_shape_code"}
            ]) {
              key
              value
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  `,

  /**
   * Fetch the first bundle component of a native Shopify Bundle product, by
   * the bundle's own handle. Used only as a fallback when a bundle dropdown
   * option has no custom.rosset_code metafield (i.e. the option is a native
   * Bundle, not one of our hand-tagged SKU-family products) — the component
   * product's title (e.g. "SC13R") stands in for the base code, and its own
   * price is used as a last-resort fallback if no finish-specific SKU (or
   * its "-SS" default) exists.
   */
  bundleComponentProduct: `
    query ($handle: String!) @inContext(country: ${country_code}) {
      product(handle: $handle) {
        selectedOrFirstAvailableVariant {
          components(first: 10) {
            nodes {
              quantity
              productVariant {
                id
                price { amount currencyCode }
                product {
                  title
                  handle
                }
              }
            }
          }
        }
      }
    }
  `,

  /**
   * Fetch the FULLY-RESOLVED SKU product by handle, once all options are picked.
   * Brings back everything the PDP needs to swap its UI to: price, variant,
   * description, images, delivery copy, sample link, specbook line-drawing, etc.
   */
  resolvedSkuProduct: `
    query ($handle: String!) @inContext(country: ${country_code}) {
      product(handle: $handle) {
        id
        handle
        title
        description
        tags
        availableForSale
        descriptionHtml
        priceRange { minVariantPrice { amount currencyCode } }
        compareAtPriceRange { minVariantPrice { amount currencyCode } }
        images(first: 10) {
          pageInfo { hasNextPage hasPreviousPage }
          edges { node { url } }
        }
        variants(first: 5) {
          pageInfo { hasNextPage hasPreviousPage }
          edges {
            node {
              id
              title
              sku
              selectedOptions { name value }
              image { url }
            }
          }
        }
        metafields(identifiers: [
          {namespace: "custom", key: "link_sample_product"},
          {namespace: "custom", key: "short_description"},
          {namespace: "custom", key: "faq"},
          {namespace: "custom", key: "installation"},
          {namespace: "custom", key: "technical_specification"},
          {namespace: "custom", key: "guarantee_returns"},
          {namespace: "custom", key: "guarantee_returns_gbp"},
          {namespace: "custom", key: "guarantee_returns_usa"},
          {namespace: "custom", key: "estimated_delivery"},
          {namespace: "custom", key: "by_type_code"},
          {namespace: "custom", key: "product_media"},
          {namespace: "custom", key: "line_drawing"}
        ]) { key value namespace }
        dimensionFile: metafield(namespace: "custom", key: "dimension_file") {
          type
          reference {
            ... on MediaImage { image { url altText } }
          }
        }
      }
    }
  `,

  /** Minimal lookup — used to turn a "request a sample" product ID into a link. */
  sampleProductById: `
    query getProductById($id: ID!) @inContext(country: ${country_code}) {
      product(id: $id) { title handle }
    }
  `,

  /** Hydrates by_type_code metaobject references (fixing types, etc.). */
  filterByTypeMetaobjects: `
    query getMetaobjects($ids: [ID!]!) @inContext(country: ${country_code}) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          type
          fields {
            key
            value
            reference {
              ... on Metaobject {
                id
                type
                fields { key value }
              }
            }
          }
        }
      }
    }
  `,

  /** Just the image data for one product — used when refreshing the gallery mid-config. */
  productGalleryById: `
    query ($id: ID!) @inContext(country: ${country_code}) {
      product(id: $id) {
        handle
        title
        images(first: 10) { edges { node { url } } }
        metafields(identifiers: [
          { namespace: "custom", key: "product_media" }
        ]) { key value }
      }
    }
  `,
};


/* ============================================================================
 * 3. DOM REFERENCES
 * ----------------------------------------------------------------------------
 * Every DOM query the script needs, cached in one place so template changes
 * only require touching this object.
 * ========================================================================= */

const DOM = {
  // Main product configurator (the MAIN PRODUCT PDP).
  optionAccordion: document.querySelector(".option_accordion"),
  customVariants: document.querySelectorAll(".custom-variant"),
  productWrapper: document.querySelector("product-info"),
  productForm: document.querySelector(".product-form"),
  addToCartButton: document.getElementById("CustomAddToCartButton"),

  // Share / specbook / wishlist entry points.
  cartProductShare: document.querySelector(".cart_product_share"),
  specbookButton: document.querySelector(".js_specbook_button"),
  specbookDownloadButton: document.querySelector("#js_specbook_download_button"),
  wishlistModal: document.querySelector("#wishlistModalCenter"),

  // PCC = "Powdercoat Colour" (special custom-finish flow).
  pccVariant: document.querySelector(".pcc_variant"),
  pccWrapper: document.querySelector(".pcc_wrapper"),

  // Add-on dropdowns (e.g. suggesting a matching bolt or backplate).
  // Only one is visible at a time based on selected type_code + finish group.
  backToBackDropdown: document.getElementById("dropdown-product-select"),
  sf1SingleBackDropdown: document.getElementById("sf1-single-back-dropdown-product-select"),
  sf2SingleBackDropdown: document.getElementById("sf2-single-back-dropdown-product-select"),
  pccSingleBackDropdown: document.getElementById("pcc-single-back-dropdown-product-select"),
  singleBackDropdown: document.getElementById("single-back-dropdown-product-select"),
  cartNotificationContainer: document.getElementById("cart-notification-dropdown-product"),

  entranceDropdowns: document.querySelectorAll("entrance-dropdown-product"),
};

// Lookup table mapping main Add-to-Cart dropdown selectors into one list for
// the "if user also picked an add-on, add it too" flow.
const ADDON_DROPDOWNS = [
  DOM.backToBackDropdown,
  DOM.sf1SingleBackDropdown,
  DOM.sf2SingleBackDropdown,
  DOM.pccSingleBackDropdown,
  DOM.singleBackDropdown,
];


/* ============================================================================
 * 4. CONTENT-SWAP CLASS MAP
 * ----------------------------------------------------------------------------
 * The PDP renders BOTH the Main Product's content AND empty "new" slots that
 * we populate once a SKU resolves. toggleContent(showOriginal) flips between
 * them so the user always sees a coherent set of copy.
 *
 * Ordering in `original` MUST match ordering in `new` — they're paired by index.
 * ========================================================================= */

const ContentSwap = {
  original: [
    "js_orignal_price",
    "js_orignal_short_description",
    "js_orignal_sku",
    "js_orignal_description",
    "js_orignal_faq",
    "js_orignal_installation",
    "js_orignal_technical_specification",
    "js_orignal_guarantee_returns",
    "js_orignal_delivery",
    "js_orignal_sku_product_title",
    "js_orignal_request_sample",
  ],
  new: [
    "js_price",
    "js_short_description",
    "js__sku",
    "js_description",
    "js_faq",
    "js_installation",
    "js_technical_specification",
    "js_guarantee_returns",
    "js__delivery",
    "js_sku_product_title",
    "js_request_sample",
  ],
};


/* ============================================================================
 * 5. STATE
 * ----------------------------------------------------------------------------
 * All mutable state lives here. Keeps debugging sane — `console.log(State)`
 * shows the whole picture.
 * ========================================================================= */

const State = {
  // Base product code from the option_accordion dataset (e.g. "LC-", "LV-").
  // Prefix for building the resolved SKU product handle.
  productCode: null,

  // First option value (e.g. "R10"). Used as the second tag filter when
  // fetching SKU candidates — massively reduces the result set.
  firstSkuPrefix: "",

  // In-memory list of SKU-product candidates matching productCode + firstSkuPrefix.
  // Populated once per first-option change; subsequent option filters are
  // pure client-side work against this array.
  skuCandidates: [],

  // What the user has selected so far across the option accordions.
  // Keys are input names (= metafield keys like "finish", "function"), values
  // are the selected option codes.
  selectedValues: {},

  // Concatenated handle of the currently-resolved SKU product.
  // e.g. productCode="LC-" + selectedValues={finish:"SS", function:"PSG"} → "LC-SSPSG"
  activeSkuHandle: "",

  // Full product payload from Queries.resolvedSkuProduct for the current combo.
  // Used by the specbook download to build the zap payload.
  activeSkuProduct: null,

  // Guards: we only pre-populate variants from URL params once per page load.
  urlPrefillApplied: false,
};


/* ============================================================================
 * 6. GRAPHQL CLIENT
 * ----------------------------------------------------------------------------
 * Single choke point for every Storefront API call — one place to add retries,
 * logging, auth changes, etc.
 * ========================================================================= */

async function gql(query, variables = {}) {
  const response = await fetch(STOREFRONT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": Config.STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.status}`);
  }

  const result = await response.json();
  if (result.errors) {
    console.error("[gql] GraphQL errors:", result.errors);
  }
  return result.data;
}


/* ============================================================================
 * 7. UTILITIES
 * ========================================================================= */

const Utils = {
  /** "gid://shopify/Product/12345" → "12345". */
  gidToId(gid) {
    const parts = gid.split("/");
    return parts[parts.length - 1];
  },

  /**
   * Shopify returns numeric metafield values as decimals ("600.0"). Strip the
   * trailing ".0*" so comparisons like "600" === "600.0" work.
   */
  trimTrailingZeroes(val) {
    if (val === undefined || val === null) return val;
    return String(val).replace(/\b(\d+)\.0+\b/g, "$1");
  },

  showLoader() {
    DOM.productWrapper?.classList.add("loading");
  },

  hideLoader() {
    DOM.productWrapper?.classList.remove("loading");
  },

  /**
   * Parse window.location.search into a URLSearchParams, tolerating a
   * malformed second "?" in the query string.
   *
   * Some upstream links (e.g. search/filter result links appending
   * "?sku=...") land as
   *   /products/ne53?_pos=1&_fid=1f3f640c8&_ss=c?sku=NE53-SN
   * A URL only has one real "?" — everything after the first one, including
   * that second "?", is just raw query-string text to URLSearchParams. So
   * the "_ss" param silently swallows "c?sku=NE53-SN" as its own value and
   * "sku" never exists as its own key, even though it's clearly present in
   * the URL. Normalizing any extra "?" to "&" before parsing fixes this and
   * every other param on the page, not just sku.
   */
  getSearchParams() {
    const raw = window.location.search;
    if (!raw) return new URLSearchParams(raw);
    const normalized = raw[0] + raw.slice(1).replace(/\?/g, "&");
    return new URLSearchParams(normalized);
  },
};

/**
 * Returns the finish code to use when a spare part is NOT available in the
 * same finish as the SKU product.  Priority order:
 *
 *   PSS → PC
 *   SSS → SN
 *   anything else → SS
 *
 * `skuFinishCode` is the finish code WITH its leading dash (e.g. "-PSS").
 * The returned value also includes the leading dash (e.g. "-PC").
 */
function sparePartFallbackFinishes(skuFinishCode) {
  const fc = (skuFinishCode || "").replace(/^-/, "").toUpperCase();
  if (fc === "PSS") return ["-PC", "-SS"];
  if (fc === "SSS") return ["-SN", "-SS"];
  return ["-SS"];
}

function sparePartFallbackFinish(skuFinishCode) {
  return sparePartFallbackFinishes(skuFinishCode)[0];
}



/* ============================================================================
 * 7b. BACKGROUND LOADER
 * ----------------------------------------------------------------------------
 * A small visual cue that background work is running — shown only after the
 * threshold elapses (see Config.BACKGROUND_LOADER_THRESHOLD_MS). Also pressed
 * into service when the user completes all options before background
 * pagination finishes: the main product resolve fires immediately, but we
 * signal that candidate data is still filling in behind the scenes.
 *
 * Reuses an existing DOM element if your template has one (selector
 * `.js-background-loader`), otherwise injects a minimal one on first show.
 * ========================================================================= */

const BackgroundLoader = {
  _el: null,

  _ensureEl() {
    if (this._el) return this._el;
    this._el = document.querySelector(".js-background-loader");
    if (this._el) return this._el;

    // Fallback: inject a minimal, unobtrusive indicator. Theme CSS can target
    // `.js-background-loader` to restyle; otherwise these inline styles keep it
    // tiny and positioned near the option accordion.
    const el = document.createElement("div");
    el.className = "js-background-loader";
    el.setAttribute("aria-live", "polite");
    el.setAttribute("role", "status");
    el.style.cssText =
      "display:none;align-items:center;gap:6px;font-size:12px;color:#666;" +
      "padding:4px 0;";
    el.innerHTML =
      '<span class="js-background-loader__spinner" style="' +
      "display:inline-block;width:12px;height:12px;border:2px solid #ccc;" +
      "border-top-color:#333;border-radius:50%;" +
      'animation:bgl-spin 0.8s linear infinite;"></span>' +
      '<span class="js-background-loader__label">Loading more options…</span>';

    // Keyframes — added once.
    if (!document.getElementById("bgl-spin-keyframes")) {
      const style = document.createElement("style");
      style.id = "bgl-spin-keyframes";
      style.textContent = "@keyframes bgl-spin{to{transform:rotate(360deg)}}";
      document.head.appendChild(style);
    }

    (DOM.optionAccordion || document.body).prepend(el);
    this._el = el;
    return el;
  },

  show(label = "Loading more options…") {
    const el = this._ensureEl();
    const labelEl = el.querySelector(".js-background-loader__label");
    if (labelEl) labelEl.textContent = label;
    el.style.display = "inline-flex";
  },

  hide() {
    if (this._el) this._el.style.display = "none";
  },
};


/* ============================================================================
 * 8. SKU CANDIDATE SEARCH & FILTERING
 * ----------------------------------------------------------------------------
 * The core of the "don't load everything" strategy. Explained at the top of
 * the file.
 * ========================================================================= */

const SkuCandidates = {
  /**
   * Read the first-option selected value into State.firstSkuPrefix.
   * Called at the start of updateSelections when we're on the FetchProducts step.
   */
  captureFirstPrefix() {
    State.productCode = Utils.trimTrailingZeroes(DOM.optionAccordion?.getAttribute("data-pcode") ?? null);
    const firstCustomVariant = document.querySelector(".custom-variant");
    if (!firstCustomVariant) return;

    const selectedRadio = firstCustomVariant.querySelector('input[type="radio"]:checked');
    State.firstSkuPrefix = selectedRadio ? Utils.trimTrailingZeroes(selectedRadio.value) : "";
  },

  /**
   * ─────────────────────────────────────────────────────────────────────────
   * PROGRESSIVE FETCH STRATEGY
   * ─────────────────────────────────────────────────────────────────────────
   * Some Main Products resolve to 1000–2000+ SKU candidates. At 250 SKUs per
   * Storefront API page that's 4–8 sequential requests (2–6 seconds of pure
   * waiting) before the user can click the second option.
   *
   * Instead, we do it in two phases:
   *
   *   Phase 1 (awaited)   — fetch page 1 (250 SKUs). User unblocks as soon as
   *                         this arrives. The main loader is shown during this.
   *
   *   Phase 2 (background)— continue paginating. New pages append to
   *                         State.skuCandidates as they arrive. By design we
   *                         DO NOT re-run filters when new pages land — that
   *                         could make option radios pop into existence mid-
   *                         selection. The tradeoff is briefly-hidden valid
   *                         options; acceptable per product decision.
   *
   * If the user picks a new first-option while a background loop is running,
   * _activeRunId invalidates the in-flight loop — stale pages are dropped.
   *
   * Results are cached in-memory keyed by productCode_firstOptionValue, so
   * bouncing between first options is instant.
   */

  /** In-memory cache: "LC-_R10" → Product[]. Lives for the page's lifetime. */
  _cache: new Map(),

  /**
   * Monotonically-increasing id. Each new fetch gets a fresh id; background
   * loops check this before writing back to State, so stale loops no-op.
   */
  _activeRunId: 0,

  /** Timer id for the "show tiny loader after N ms" delayed indicator. */
  _slowTimer: null,

  /** True while any background pagination is still running. */
  _backgroundRunning: false,

  _cacheKey(productCode, prefix) {
    return `${productCode}_${prefix}`;
  },

  /**
   * Fetch SKU candidates for (productCode + first-option prefix).
   * Awaits page 1 only — subsequent pages load in the background.
   *
   * @param {string} sku First-option prefix (e.g. "R10").
   */
  async fetch(sku) {
    const runId = ++this._activeRunId;
    const key = this._cacheKey(State.productCode, sku);

    console.group(`[SkuCandidates.fetch]`);
    console.log("RunId:", runId, "Key:", key);

    try {
      // ── Cache ─────────────────────────────
      if (this._cache.has(key)) {
        console.log("✅ Cache HIT");

        State.skuCandidates = this._cache.get(key);

        if (!State.urlPrefillApplied) {
          // Pre-seed State.selectedValues from URL params so finish is
          // included in the gallery match, not just the first-option.
          UrlPrefill.seedSelectedValuesFromUrl();

          if (State.skuCandidates.length > 0 && !Gallery.shouldPreserveUrlFinishImage()) {
            await Gallery.updateFromCurrentSelection();
          }
        }

        return State.skuCandidates;
      }

      console.log("❌ Cache MISS");

      Utils.showLoader();

      State.skuCandidates = [];

      const cleanCode = Utils.trimTrailingZeroes(State.productCode);
      const cleanSku = Utils.trimTrailingZeroes(sku);
      const cleanSkuNoHyphen = cleanSku.replace("-", "");

      // Shopify's `tag:` search tokenizes on hyphens, so a query for
      // "2020" / "50" can still return products like "2020-50-B-VBB"
      // (it shares the same tokens as "2020-50-VBB"). The GraphQL query
      // is therefore only a coarse pre-filter — we do the real, exact
      // match ourselves against each product's actual `tags` array.
      /*  const exactTagMatch = (tags) =>
          Array.isArray(tags) &&
          tags.includes(cleanCode) &&
          (tags.includes(cleanSku) || tags.includes(cleanSkuNoHyphen));*/


      const exactTagMatch = (tags) => {
        if (!Array.isArray(tags)) return false;
        const lower = tags.map((t) => String(t).toLowerCase());
        const code = String(cleanCode).toLowerCase();
        const sku = String(cleanSku).toLowerCase();
        const skuNoHyphen = String(cleanSkuNoHyphen).toLowerCase();
        return lower.includes(code) && (lower.includes(sku) || lower.includes(skuNoHyphen));
      };

      const tagQuery =
        `tag:'${cleanCode}' AND (tag:'${cleanSkuNoHyphen}' OR tag:'${cleanSku}') ` +
        `AND product_type:'${Config.SHOPIFY_SKU_PRODUCT_TYPE}'`;

      console.log("Query:", tagQuery);

      const pageOne = await this._fetchPage(tagQuery, null, exactTagMatch);

      // Race condition check
      if (runId !== this._activeRunId) {
        console.warn("⚠ Stale request, ignoring");
        return [];
      }

      State.skuCandidates = pageOne.items;

      console.log("Page 1 items:", pageOne.items.length);

      if (!State.urlPrefillApplied) {
        // Pre-seed State.selectedValues from URL params so finish (and function)
        // are factored into the gallery match immediately, not just backplate.
        UrlPrefill.seedSelectedValuesFromUrl();

        if (pageOne.items.length > 0 && !Gallery.shouldPreserveUrlFinishImage()) {
          await Gallery.updateFromCurrentSelection();
        }
      }

      if (pageOne.hasNextPage) {
        console.log("🔄 Background fetch starting...");
        this._startBackgroundFetch(runId, tagQuery, pageOne.endCursor, key, exactTagMatch);
      } else {
        this._cache.set(key, [...pageOne.items]);
      }

      return State.skuCandidates;

    } catch (error) {
      console.error("❌ FETCH FAILED:", error);

      return [];

    } finally {
      // ✅ ALWAYS runs
      Utils.hideLoader();
      BackgroundLoader.hide();

      console.log("🧹 Loaders cleared");
      console.groupEnd();
    }
  },

  /**
   * Pull one page. Shared by both the awaited page-1 call and the background
   * loop, so pagination logic stays in one place.
   */
  async _fetchPage(tagQuery, after, exactTagMatch) {
    const data = await gql(Queries.skuCandidates, {
      first: Config.SKU_FETCH_PAGE_SIZE,
      query: tagQuery,
      after,
    });
    const products = data?.products;
    if (!products?.edges?.length) {
      return { items: [], hasNextPage: false, endCursor: null };
    }

    // Shopify's search matched on tokens (coarse). If an exact-match
    // predicate was supplied, narrow down to real, literal tag matches
    // before mapping — this is what excludes e.g. "2020-50-B-VBB" when
    // the caller asked for "2020-50-VBB".
    const edges = exactTagMatch
      ? products.edges.filter((edge) => exactTagMatch(edge.node.tags))
      : products.edges;

    return {
      items: edges.map((edge) => this._mapProductNode(edge.node)),
      hasNextPage: products.pageInfo.hasNextPage,
      endCursor: products.pageInfo.endCursor,
    };
  },

  /**
   * Background pagination loop. Does NOT block the user. Appends each page
   * to State.skuCandidates. If runId has been invalidated (user changed
   * first option), the loop exits without touching State.
   */
  _startBackgroundFetch(runId, tagQuery, startCursor, cacheKey, exactTagMatch) {
    this._backgroundRunning = true;

    // Show the tiny loader only if background work is still running after
    // the configured threshold — avoids UI flicker for fast queries.
    this._slowTimer = setTimeout(() => {
      if (this._backgroundRunning && runId === this._activeRunId) {
        BackgroundLoader.show();
      }
    }, Config.BACKGROUND_LOADER_THRESHOLD_MS);

    (async () => {
      let after = startCursor;
      let hasNextPage = true;
      const accumulated = [...State.skuCandidates];

      while (hasNextPage) {
        // Abandon loop if user moved on.
        if (runId !== this._activeRunId) return;

        try {
          const page = await this._fetchPage(tagQuery, after, exactTagMatch);
          if (runId !== this._activeRunId) return;

          accumulated.push(...page.items);
          // Append to the live list so freshly-arrived candidates are
          // usable when the user finally picks all options. We do NOT
          // re-filter visible accordions here (per product decision).
          State.skuCandidates = accumulated;

          hasNextPage = page.hasNextPage;
          after = page.endCursor;
        } catch (error) {
          console.error("[SkuCandidates background] failed:", error.message);
          break;
        }
      }

      // Cache the full list only if the loop completed for the active run.
      if (runId === this._activeRunId) {
        this._cache.set(cacheKey, accumulated);
      }
    })().finally(() => {
      // Only clear flags if we're still the active run — otherwise a newer
      // loop owns them.
      if (runId === this._activeRunId) {
        this._backgroundRunning = false;
        clearTimeout(this._slowTimer);
        BackgroundLoader.hide();

        // Re-filter all currently visible sections now that the full candidate
        // list is available. On first-option selection the section-2 filter ran
        // against only 250 SKUs — valid options in later pages were wrongly hidden.
        //
        // For each section, pass only the selections from PRECEDING sections.
        // Passing the section's own key would collapse everything to "only show
        // the currently-selected value" — that's wrong while the user can still
        // change their mind.
        // ✅ FIX: Re-filter every section that is "in scope" — i.e. whose
        // immediately-preceding section has a selection in State.selectedValues.
        // Previously this block was gated on State.selectedValues being non-empty
        // AND on section.style.display !== "none" (the container element, which
        // our code never sets to none). The display guard was harmless in most
        // cases but could skip sections whose ALL options had been shown via the
        // "show all while background running" path above (those sections have
        // display:block on the container but unfiltered options inside).
        //
        // We now explicitly re-filter every section from index 1 up to and
        // including the one immediately after the last selection (i.e. the next
        // section the user hasn't chosen yet), so that even partially-revealed
        // sections get correctly narrowed once the full candidate list is in.
        if (Object.keys(State.selectedValues).length > 0) {
          DOM.customVariants.forEach((section, i) => {
            if (i === 0) return; // section 0 is the primary filter — never re-filter it

            // Build precedingValues using ONLY selections from sections that appear
            // BEFORE index i in DOM order. The old approach (all State.selectedValues
            // minus own key) incorrectly included selections from LATER sections
            // (e.g. finish: '-SBU' when re-filtering function_code), causing the
            // function list to be over-constrained by a stale finish selection left
            // over from URL prefill — showing 16 instead of the correct 23.
            const getSectionKey = (sec) => sec?.querySelector("input")?.name || sec?.getAttribute("data-name");
            const sectionKey = getSectionKey(section);
            const precedingValues = {};
            for (let j = 0; j < i; j++) {
              const prevKey = getSectionKey(DOM.customVariants[j]);
              if (prevKey && State.selectedValues[prevKey] !== undefined) {
                precedingValues[prevKey] = State.selectedValues[prevKey];
              }
            }

            // Only re-filter a section if at least one preceding option was
            // chosen (otherwise there is nothing to filter against).
            if (Object.keys(precedingValues).length === 0) return;

            // Skip sections that are still fully hidden (not yet reachable by the
            // user) — i.e. sections beyond the next-to-select one.  A section is
            // "reachable" if ALL keys corresponding to sections before it in the
            // DOM are present in State.selectedValues.  We approximate this by
            // checking whether the section immediately before this one has a
            // selection: if DOM.customVariants[i-1]'s data-name key is absent
            // from State.selectedValues (and i-1 > 0), the user hasn't reached
            // section i yet.
            if (i > 1) {
              const prevSectionKey = getSectionKey(DOM.customVariants[i - 1]);
              if (prevSectionKey && !(prevSectionKey in State.selectedValues)) return;
            }

            SkuCandidates.filterOptions(section, precedingValues, "backgroundFetch.finally");
          });
        }
      }
    });
  },

  /** True if a background pagination loop is currently running. */
  isBackgroundRunning() {
    return this._backgroundRunning;
  },

  /**
   * Given a raw Storefront product node, flatten its option metafields into
   * a normalized candidate record.
   */
  _mapProductNode(node) {
    const metafields = node.metafields.reduce((acc, meta) => {
      if (!meta) return acc;
      // length_code / handing_code come back as "600.0" style — truncate.
      if (meta.key === "length_code") {
        acc[meta.key] = Math.trunc(meta.value);
      } else {
        acc[meta.key] = meta.value;
      }
      return acc;
    }, {});

    return {
      id: node.id,
      handle: node.handle,
      title: node.title,
      // Contextual to @inContext(country: ...) — false when this SKU is
      // excluded from sale in the shopper's current market. In practice,
      // market-excluded SKUs are simply absent from the search results
      // entirely (Shopify filters them out of products(query:...) before
      // this ever runs), so this mostly stays true; kept as a defensive
      // check in case that ever isn't the case for a given catalog setup.
      availableForSale: node.availableForSale !== false,
      rosset_code: metafields.rosset_code,
      function_code: Utils.trimTrailingZeroes(metafields.function_code),
      finish: metafields.finish_code,
      timber: metafields.timber_code,
      filter_by_type: metafields.by_type_code,
      type_code: metafields.type_code,
      length_code: Utils.trimTrailingZeroes(metafields.length_code),
      handing_code: Utils.trimTrailingZeroes(metafields.handing_code),
      backplate_code: Utils.trimTrailingZeroes(metafields.backplate_shape_code),
    };
  },

  /**
   * Hide option radios that would produce no valid SKU combination given the
   * user's current selection. Options that become invalid are also unchecked
   * so stale state doesn't survive a change.
   */
  filterOptions(section, selectedValues, _caller) {
    const inputs = section.querySelectorAll("input");
    const getSectionKey = (sec) => sec?.querySelector("input")?.name || sec?.getAttribute("data-name");
    const _sectionName = getSectionKey(section) || "?";

    // If no selections have been made yet, show all options
    const hasSelections = Object.keys(selectedValues).length > 0;

    // Shared with both the main per-input validity check below and the
    // with_pcc branch (which needs to know validity for the PCC-tile
    // visibility decision even though it never hides the swatch itself).
    const normalize = (v) =>
      v === null || v === undefined ? "" : String(v).toLowerCase().replace(/^-+/, "");

    const isCandidateValid = (key, value) =>
      State.skuCandidates.some((product) => {
        const selectionsMatch = Object.entries(selectedValues).every(([k, v]) => {
          const prodVal = normalize(product[k]);
          const selectedVal = normalize(v);
          return prodVal === selectedVal;
        });
        const prodVal = normalize(product[key]);
        const thisOptionVal = normalize(value);
        return selectionsMatch && (prodVal === thisOptionVal);
      });

    // ✅ FIX: tracks whether ANY ".with_pcc" swatch in this section is a
    // valid candidate for the current selection, even though (see below)
    // with_pcc swatches are always force-shown and never individually
    // hidden. Without this, the "Powder Coat" tile below only ever
    // consulted plain (".non_pcc") finishes to decide whether to show
    // itself — so for a backplate/function combo whose ONLY valid
    // finishes are powdercoat colours (no plain finish at all), the tile
    // stayed hidden and the shopper had no way to ever open the PCC
    // wrapper and pick a colour like "MOM" manually. URL-prefill never
    // hit this because it opens the wrapper directly via
    // UrlPrefill._revealPccForInput(), bypassing the tile entirely.
    let anyPccValid = false;

    inputs.forEach((input, index) => {
      // ✅ FIX: Powdercoat colour swatches (".with_pcc") aren't plain finish
      // options — they live behind the "Powder Coat" tile and are never
      // represented as their own `finish` value on a normal SKU candidate,
      // so the validity check below always fails for them. Left unguarded,
      // that force-hides every PCC swatch (display:none) as soon as any
      // selection is made elsewhere, so the PCC wrapper opens empty even
      // though PccFlow.bind() correctly reveals the wrapper itself.
      //
      // IMPORTANT: we can't just `return` here and leave them untouched —
      // bootstrapConfigurator()'s "hide sections 2+ when no SKU in URL"
      // step (and the equivalent reset branch in updateSelections) already
      // force every input in this section, PCC ones included, to
      // display:none on load/reset. If we skip them in filterOptions they
      // never get un-hidden again. So instead we always restore them to
      // display:block here — their *overall* visibility is still gated by
      // the pccWrapper/pccVariant "d-none" toggle (PccFlow, _revealPccForInput),
      // this just makes sure they're never stuck hidden underneath it.
      if (input.classList.contains("with_pcc")) {
        input.parentElement.style.display = "block";
        // Still check (but don't act on) validity here — this is what
        // anyPccValid feeds into below, so the "Powder Coat" tile knows
        // to show itself even when it's the ONLY valid finish path.
        if (isCandidateValid(input.name, input.value)) {
          anyPccValid = true;
        }
        return;
      }

      const key = input.name;
      const value = input.value;

      // A candidate field can be `undefined` (the key doesn't exist on the
      // mapped product at all — a structural mismatch, should never match)
      // or `null` (the key exists but Shopify has no value set for it — a
      // real, meaningful "blank" that should compare equal to an empty
      // string, e.g. the Function accordion's blank/no-function tile).
      // Without this distinction, `null` stringifies to the literal text
      // "null" and can never match anything, silently excluding every
      // candidate with a genuinely blank field from validity checks.
      const isValid = isCandidateValid(key, value);

      input.parentElement.style.display = "block";
      if (!isValid && hasSelections) {
        // ✅ FIX (market availability): previously an invalid-but-URL-linked
        // option (e.g. Finish "SN" from ?sku=CCL-PR64W-E-SN) was deliberately
        // left visible/checked here to avoid flicker while URL-prefill was
        // still sequencing through sections. That made a market-excluded
        // SKU *look* selectable even though resolveSkuProduct() would later
        // silently disable the cart button for it.
        //
        // By the time this section is filtered, SkuCandidates.fetch() has
        // already been awaited and UrlPrefill.applyRestAfterFetch() has
        // already waited out any background pagination — so State.skuCandidates
        // is complete and `isValid` here is a real, final answer, not a
        // transient "still loading" state. So we no longer special-case the
        // URL target: if it has zero valid (sellable-in-market) candidates,
        // hide + uncheck it like any other invalid option.
        input.checked = false;
        delete selectedValues[key];
        input.parentElement.style.display = "none";
      } else {
        input.parentElement.style.display = "block";
      }
    });

    // ── DIAGNOSTIC LOGGING ──────────────────────────────────────────────────
    if (_sectionName === "function_code") {
      const visibleCount = [...inputs].filter(
        (inp) => !inp.classList.contains("with_pcc") && inp.parentElement.style.display !== "none"
      ).length;
      console.warn(
        `[filterOptions] section="${_sectionName}" caller="${_caller || 'unknown'}"`,
        `selectedValues=`, JSON.parse(JSON.stringify(selectedValues)),
        `candidates=${State.skuCandidates.length}`,
        `visibleCount=${visibleCount}`
      );
      console.trace("filterOptions call stack");
    }
    // ── END DIAGNOSTIC LOGGING ──────────────────────────────────────────────

    // Show/hide the static PCC tile based on whether any finish input is visible
    const pccTile = section.querySelector('.pcc_variant');
    if (pccTile) {
      const anyFinishVisible = [...section.querySelectorAll('input.non_pcc')].some(
        (inp) => inp.parentElement.style.display !== "none"
      );
      // ✅ FIX: if a powdercoat colour is already checked in this section (e.g.
      // UrlPrefill._revealPccForInput() just opened the wrapper and checked a
      // ".with_pcc" swatch like "BKA" for a product whose only valid finishes
      // ARE powdercoat colours), don't slam the wrapper back to "d-none" just
      // because there are no plain/non_pcc finishes to show. filterOptions()
      // runs again on this exact section as a side-effect of processing the
      // section before it (see the `DOM.customVariants[i + 1]` call above),
      // including on the very same updateSelections invocation that the PCC
      // reveal itself triggered — without this guard that re-run immediately
      // undoes the reveal, leaving the colour swatch checked/valid but its
      // wrapper still hidden.
      const pccColourChosen = !!section.querySelector('input.with_pcc:checked');
      // ✅ FIX: also show the tile when no colour is checked YET but at least
      // one ".with_pcc" swatch (anyPccValid, computed above) is a valid
      // candidate for the current selection. Previously this only looked at
      // plain (".non_pcc") finishes, so a backplate/function combo whose
      // ONLY valid finish path is powdercoat (e.g. PS6 + 4W-E → MOM) hid the
      // "Powder Coat" tile entirely on manual selection — there was no
      // finish left to click to even open the wrapper. URL-prefill (?sku=...)
      // never hit this because it opens the wrapper directly, bypassing the
      // tile.
      pccTile.style.display = (anyFinishVisible || pccColourChosen || anyPccValid) ? "block" : "none";

      // ✅ FIX: PccFlow.bind()'s click handler toggles pccTile's "selected"
      // class and un-hides pccWrapper *synchronously*, then dispatches a
      // change event to recompute state. That dispatch runs updateSelections
      // → filterOptions() again on this very same section — and until now,
      // whenever there was no plain finish visible and no colour checked yet
      // (exactly the state right after opening the tile, before the shopper
      // has picked a colour), this block unconditionally undid the open:
      // removed "selected" and re-hid the wrapper. The tile looked like it
      // didn't respond to clicks — it opened for a single frame and then
      // slammed shut. pccManuallyOpen lets a click-driven open survive this
      // recompute as long as PCC is still a valid path (anyPccValid); it's
      // read from the tile's own classList, which PccFlow.bind() already set
      // *before* triggering this recompute, so it accurately reflects the
      // shopper's most recent click (open or closed).
      const pccManuallyOpen = pccTile.classList.contains("selected") && anyPccValid;

      if (!anyFinishVisible && !pccColourChosen && !pccManuallyOpen) {
        pccTile.classList.remove("selected");
        DOM.pccWrapper?.classList.add("d-none");
      } else if (pccColourChosen || pccManuallyOpen) {
        pccTile.classList.add("selected");
        DOM.pccWrapper?.classList.remove("d-none");
      }
    }

  },
};


/* ============================================================================
 * 9. OPTION SELECTION ORCHESTRATOR
 * ----------------------------------------------------------------------------
 * Called on every radio change inside .custom-variant. Walks through each
 * option accordion in order, driving candidate fetches, filtering downstream
 * options, and (when the combo is complete) resolving the SKU product.
 * ========================================================================= */

// Monotonically-increasing counter. Each updateSelections call gets a fresh seq;
// after any await it checks whether a newer call has started — if so it exits.
// This prevents two concurrent async invocations from racing over State.selectedValues,
// which happens when the user changes option 1 (triggering a fetch) and then clicks
// option 2 before the fetch returns (sections 2+ are still visible from a prior selection).
let _updateSelectionsSeq = 0;

async function updateSelections() {
  // `this` is the <input> that changed.
  const seq = ++_updateSelectionsSeq;

  // Immediately clear the SKU display so changing any option never shows a stale SKU
  ProductView._clearSku();

  // The shopper is actively choosing a value in this section now, so any
  // "SKU didn't specify this" prompt we added during URL prefill is stale.
  UrlPrefill._clearNeedsSelection(this.closest(".custom-variant"));

  // PCC flow: some finishes explicitly reset the PCC (powdercoat colour) state.
  if (DOM.pccVariant && DOM.pccWrapper && this.classList.contains("non_pcc")) {
    DOM.pccVariant.classList.remove("selected");
    DOM.pccWrapper.classList.add("d-none");
  }

  // Reset — we'll rebuild from scratch by walking the accordion sections.
  State.selectedValues = {};
  const parentSection = this.closest(".accordion-item");

  for (let i = 0; i < DOM.customVariants.length; i++) {
    const section = DOM.customVariants[i];

    // FetchProducts flag marks the section whose selection triggers the
    // network hit for SKU candidates. By convention this is the first section.
    if (parentSection.classList.contains("FetchProducts") && i === 0) {
      Utils.showLoader();
      SkuCandidates.captureFirstPrefix();
      const runId = SkuCandidates._activeRunId + 1;
      await SkuCandidates.fetch(State.firstSkuPrefix);

      // If the user clicked another option while we were fetching, a newer
      // updateSelections call is now in charge — abandon this stale run.
      if (seq !== _updateSelectionsSeq) {
        console.log("[updateSelections] stale run abandoned (seq mismatch)");
        return;
      }

      if (runId !== SkuCandidates._activeRunId) {
        console.log("[updateSelections] newer fetch in progress, aborting stale execution");
        return;
      }
      UrlPrefill.applyRestAfterFetch();

      // ── Guard: this first-option value returned no SKU candidates ──
      // When skuCandidates is empty the Backplate itself is still a valid
      // selection — it simply has no matching Function/Finish combos (e.g.
      // "-PS5"). Do NOT hide or uncheck the Backplate radio here. Instead,
      // leave it checked and let the natural loop continue: filterOptions()
      // will hide every Function option (no candidates to validate against),
      // so no Function input ends up checked, which triggers the existing
      // "first unselected section" else-branch that already hides+resets
      // every downstream section (Function, Finish) automatically.
      if (State.firstSkuPrefix && State.skuCandidates.length === 0) {
        const selectedBackplate = section.querySelector('input[type="radio"]:checked');
        if (selectedBackplate) {
          console.warn(`[updateSelections] "${selectedBackplate.value}" has no downstream SKU candidates — downstream sections will be cleared`);
        }
        State.firstSkuPrefix = "";
        Utils.hideLoader();
      }
    }

    const selectedInput = section.querySelector("input:checked");

    if (selectedInput) {
      State.selectedValues[selectedInput.name] = selectedInput.value;

      // Reveal the next section and filter its options based on what's chosen so far.
      if (i + 1 < DOM.customVariants.length) {
        DOM.customVariants[i + 1].style.display = "block";

        // Un-hide all non-PCC options in the next section first so filterOptions starting state is clean
        DOM.customVariants[i + 1].querySelectorAll("input").forEach((opt) => {
          if (!opt.classList.contains("with_pcc")) {
            opt.parentElement.style.display = "block";
          }
        });

        SkuCandidates.filterOptions(DOM.customVariants[i + 1], State.selectedValues, `updateSelections(i=${i})`);
      }

      // Reveal PCC toggle when the finish section is being revealed (multi-option)
      // or IS the only/current section (finish-only products where i === length-1).
      if (i + 1 >= DOM.customVariants.length - 1 && DOM.pccVariant) {
        DOM.pccVariant.classList.remove("d-none");
      }
    } else {
      // First unselected section → hide + reset every section beyond this one.
      for (let j = i + 1; j < DOM.customVariants.length; j++) {
        const opts = DOM.customVariants[j].querySelectorAll("input");
        opts.forEach((opt) => {
          opt.parentElement.style.display = "none";
          opt.checked = false;
        });
        // Reset PCC tile too
        const pccTile = DOM.customVariants[j].querySelector('.pcc_variant');
        if (pccTile) {
          pccTile.style.display = "none";
          pccTile.classList.remove("selected");
          DOM.pccWrapper?.classList.add("d-none");
        }
      }
      break;
    }
  }

  if (UrlPrefill._inProgress) return;

  if (DOM.addToCartButton) {
    CartButton.disable();
  }

  const allOptionsChosen = DOM.customVariants.length === Object.keys(State.selectedValues).length;
  if (allOptionsChosen) {
    Utils.showLoader();
    State.activeSkuHandle = `${State.productCode}${Object.values(State.selectedValues).join("")}`;

    // If background pagination of the candidate list is still running at this
    // point, surface the tiny loader so the user sees that *something* is
    // still filling in. The resolve-by-handle call itself does NOT wait on
    // candidates — it runs in parallel, because the handle is enough to fetch
    // the final SKU product directly.
    if (SkuCandidates.isBackgroundRunning()) {
      BackgroundLoader.show();
    }

    await resolveSkuProduct();
  } else {
    // Partial selection — revert to the MAIN product's static content.
    // No complete SKU selected → always disable specbook/wishlist controls.
    ProductView._clearSku();
    syncSpecbookButtonState(false);
    ContentSwap.toOriginal();
  }
}


/* ============================================================================
 * 10. RESOLVE SKU PRODUCT
 * ----------------------------------------------------------------------------
 * Once every option is chosen we fetch the full SKU product by handle and
 * push its data into the PDP UI.
 * ========================================================================= */

async function resolveSkuProduct() {
  if (!State.activeSkuHandle) {
    ProductView._clearSku();
    return;
  }

  try {
    const matchedCandidate = State.skuCandidates.find((product) =>
      Object.entries(State.selectedValues).every(([k, v]) => {
        if (product[k] === undefined) return false;
        const prodVal = String(product[k]).toLowerCase().replace(/^-+/, "");
        const selectedVal = String(v).toLowerCase().replace(/^-+/, "");
        return prodVal === selectedVal;
      })
    );
    const handle = matchedCandidate?.handle || State.activeSkuHandle;
    const data = await gql(Queries.resolvedSkuProduct, { handle });

    // Independent of product success/failure, reset add-on dropdown visibility.
    AddonDropdowns.updateVisibility();

    const sku = data?.product?.variants?.edges?.[0]?.node?.sku;
    if (data?.product && sku) {
      State.activeSkuProduct = data.product;
      await ProductView.render(data.product);
      if (data.product.availableForSale) {
        CartButton.enableForProduct(data.product);
      } else {
        CartButton.disable("Sold out");
      }
      // Valid SKU product found — enable specbook/wishlist controls.
      syncSpecbookButtonState(true);
    } else {
      // SKU resolved to a handle but no matching Shopify product found or no valid SKU.
      State.activeSkuProduct = null;
      ProductView._clearSku();
      CartButton.disable("Not Available");
      syncSpecbookButtonState(false);
      ContentSwap.toOriginal();
    }
  } catch (error) {
    console.error("[resolveSkuProduct] failed:", error.message);
    State.activeSkuProduct = null;
    ProductView._clearSku();
    CartButton.disable("Not Available");
    syncSpecbookButtonState(false);
    ContentSwap.toOriginal();
  } finally {
    Utils.hideLoader();
    Gallery.clearInitialSkeleton();
  }
}


/* ============================================================================
 * 11. ADD-ON DROPDOWNS
 * ----------------------------------------------------------------------------
 * Depending on the user's chosen finish GROUP (SF1/SF2/PCC/other) + the chosen
 * TYPE CODE (-B back-to-back, -E single-sided entrance), we surface at most
 * one suggested-add-on dropdown. This is a merchandising feature — e.g. "you
 * bought a back-to-back handle set, here's the matching single-sided for the
 * other door".
 * ========================================================================= */

const AddonDropdowns = {
  updateVisibility() {
    const selectedFinish = document.querySelector('input[name="finish"]:checked');
    const selectedType = document.querySelector('input[name="type_code"]:checked');
    if (!selectedFinish || !selectedType) {
      ADDON_DROPDOWNS.forEach((el) => el && (el.style.display = "none"));
      return;
    }

    const group = selectedFinish.closest(".accordion_varient")?.getAttribute("data-group");
    const typeValue = selectedType.value;

    const show = (el) => el && (el.style.display = "block");
    const hide = (el) => el && (el.style.display = "none");

    // Start from clean slate — hide all, then selectively show one.
    ADDON_DROPDOWNS.forEach(hide);

    let targetDropdown = null;
    if (typeValue === "-B") {
      targetDropdown = DOM.backToBackDropdown;
    } else {
      switch (group) {
        case "SF1": targetDropdown = DOM.sf1SingleBackDropdown; break;
        case "SF2": targetDropdown = DOM.sf2SingleBackDropdown; break;
        case "PCC": targetDropdown = DOM.pccSingleBackDropdown; break;
        default: targetDropdown = DOM.singleBackDropdown;
      }
    }

    if ((!targetDropdown || targetDropdown.options?.length <= 1) && DOM.backToBackDropdown?.options?.length > 1) {
      targetDropdown = DOM.backToBackDropdown;
    }

    show(targetDropdown);
  },

  /**
   * If the user picked an option in any visible add-on dropdown, POST each
   * resolved component SKU to /cart/add.js.
   *
   * Bundle products (no data-dcode): each component SKU is resolved
   * finish-aware (finish → SS → component direct) and added separately with
   * its bundle-defined quantity.
   *
   * Non-bundle options (have data-dcode): added as a single item using the
   * option's own baked-in variant id (existing behaviour).
   *
   * All POSTs are fully awaited sequentially to avoid Shopify cart race
   * conditions (two concurrent /cart/add.js calls can silently clobber each
   * other). Returns a Promise so bindClick() can await the full sequence
   * before submitting the main product.
   */
  async addSelectedToCart() {
    const selectedOption = this._findActiveSelection();
    if (!selectedOption?.value) return;

    const mainTitle = State.activeSkuProduct?.title || document.querySelector(".product__title h1")?.textContent?.trim() || "";
    const selectedFinish = document.querySelector('input[name="finish"]:checked');
    const finishCode = selectedFinish ? String(selectedFinish.value || "").trim() : "";
    const finishName = selectedFinish?.title || selectedFinish?.getAttribute("title") || "";

    const properties = {};
    if (mainTitle) properties["Main Product"] = mainTitle;
    if (finishName) properties["Finish"] = finishName;
    if (this._activeGroupId) properties["_group_id"] = this._activeGroupId;

    const qtyInput = document.querySelector('.quantity__input[name="quantity"], [name="quantity"]');
    const mainQty = Math.max(1, parseInt(qtyInput?.value, 10) || 1);

    const baseCode = selectedOption.getAttribute("data-dcode");

    if (baseCode) {
      // ── Non-bundle SKU option: use the option's own static variant id.
      const staticVariantId = selectedOption.getAttribute("data-variant-id");
      if (!staticVariantId) return;
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: staticVariantId, quantity: mainQty, properties }),
      })
        .then((r) => r.json())
        .then((product) => this._renderCartNotification(product))
        .catch((err) => console.error("[AddonDropdowns] cart add failed:", err));
    }

    // ── Bundle product: resolve each component SKU and add separately.
    const bundleHandle = selectedOption.getAttribute("data-dhandle");
    if (!bundleHandle) return;

    let components = [];
    try {
      const data = await gql(Queries.bundleComponentProduct, { handle: bundleHandle });
      components = data?.product?.selectedOrFirstAvailableVariant?.components?.nodes || [];
    } catch (e) {
      console.error("[AddonDropdowns] bundle component fetch failed:", e);
    }

    if (!components.length) return;

    const fetchProductByHandle = async (h) => {
      try {
        const data = await gql(Queries.resolvedSkuProduct, { handle: h });
        return data?.product || null;
      } catch (e) { return null; }
    };

    for (const component of components) {
      const componentTitle = component?.productVariant?.product?.title;
      const componentQty = Number(component?.quantity) > 0 ? Number(component.quantity) : 1;
      const componentVariantIdRaw = component?.productVariant?.id;
      const componentVariantId = componentVariantIdRaw ? Utils.gidToId(componentVariantIdRaw) : null;

      if (!componentTitle && !componentVariantId) continue;

      let resolvedVariantId = null;
      let actualComponentFinish = finishCode;

      if (componentTitle) {
        // 1. Try same-finish SKU
        let skuProduct = null;
        if (finishCode) {
          skuProduct = await fetchProductByHandle(`${componentTitle}${finishCode}`);
        }
        // 2. Fallback: PSS→PC, SSS→SN, else→SS
        if (!skuProduct) {
          const fallbackFinish = sparePartFallbackFinish(finishCode);
          skuProduct = await fetchProductByHandle(`${componentTitle}${fallbackFinish}`);
          if (skuProduct) actualComponentFinish = fallbackFinish.replace(/^-/, "");
        }
        // 3. Fallback to component's own variant
        if (skuProduct) {
          const varNode = skuProduct.variants?.edges?.[0]?.node;
          if (varNode?.id) resolvedVariantId = Utils.gidToId(varNode.id);
        }
      }

      // Final fallback: component's own direct variant from the bundle
      if (!resolvedVariantId && componentVariantId) {
        resolvedVariantId = componentVariantId;
      }

      if (!resolvedVariantId) continue;

      const componentProps = { ...properties };
      if (actualComponentFinish) {
        componentProps["Finish"] = actualComponentFinish.replace(/^-/, "");
      }

      try {
        const resp = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: resolvedVariantId, quantity: componentQty * mainQty, properties: componentProps }),
        });
        const product = await resp.json();
        this._renderCartNotification(product);
      } catch (err) {
        console.error("[AddonDropdowns] bundle component cart add failed:", err);
      }
    }
  },

  /** Return the first VISIBLE dropdown with a real (non-placeholder) selection. */
  _findActiveSelection() {
    const dropdowns = document.querySelectorAll(".form-select-dropdown-product, .entrance-dropdown-product");
    for (const dd of dropdowns) {
      if (!dd) continue;
      if (dd.selectedIndex > 0) {
        const opt = dd.options[dd.selectedIndex];
        if (opt && opt.value && !opt.textContent.toLowerCase().includes("select ")) {
          return opt;
        }
      }
    }
    return null;
  },

  /**
   * Helper function to calculate add-on price: Product Price * Bundle Quantity
   * @param {number} productPrice
   * @param {number} bundleQuantity
   * @returns {number}
   */
  calculateAddonPrice(productPrice, bundleQuantity) {
    const price = parseFloat(productPrice) || 0;
    const qty = parseFloat(bundleQuantity) > 0 ? parseFloat(bundleQuantity) : 1;
    return price * qty;
  },

  /**
   * Resolve the live, finish-aware price for a bundle/add-on dropdown pick.
   *
   * Isolated from _updateAddonDropdownPricing() so it can be called from any
   * dropdown's `change` handler (or reused elsewhere) without depending on
   * that function's option-rendering/state. Steps:
   *   1. Read the product selected from the bundle dropdown (`selectedOption`).
   *      If it has no custom.rosset_code (i.e. it's a native Shopify Bundle
   *      rather than one of our SKU-family products), fall back to the title
   *      of that bundle's own first component product as the base code.
   *   2. Read the finish the shopper currently has selected on the main product.
   *   3. Build the SKU/handle for that bundle product in that finish
   *      (base code from data-dcode + the finish code).
   *   4. If no product exists for that finish, fall back to the SKU built
   *      with the default finish "-SS". If that also doesn't exist and the
   *      base code came from the native-Bundle fallback (step 1), use the
   *      bundle component's own direct price instead of the option's static
   *      baked-in price (which is $0 for a native Bundle wrapper product).
   *   5. Fetch the resolved product's price via the Storefront API.
   *   6. Multiply that unit price by the bundle's own component quantity if
   *      resolved via the native-Bundle fallback (step 1), otherwise by the
   *      buyer-selected quantity.
   *
   * @param {HTMLOptionElement} selectedOption - the <option> chosen in a bundle dropdown.
   * @param {number} [quantity=1] - quantity of the bundle item being added.
   * @returns {Promise<{price: number, unitPrice: number, currency: string|null, handle: string|null} | null>}
   *          `price` is already multiplied by quantity. Returns null only when
   *          nothing was selected at all.
   */
  /**
   * Resolve the live, finish-aware price for a bundle/add-on dropdown pick.
   *
   * For non-bundle options (have data-dcode): uses the single base code and
   * buyer-selected quantity — same as before.
   *
   * For native Shopify Bundle options (no data-dcode): fetches ALL components,
   * resolves a finish-aware price for each one (finish → SS → component
   * direct price), and returns a summed total across all components × their
   * bundle-defined quantities.
   *
   * @param {HTMLOptionElement} selectedOption
   * @param {number} [quantity=1] — buyer-selected quantity (used for non-bundle options).
   * @returns {Promise<{price, unitPrice, currency, handle, variantId, name}|null>}
   */
  async resolveBundleItemPrice(selectedOption, quantity = 1) {
    if (!selectedOption || !selectedOption.value) return null;

    const qty = Number(quantity) > 0 ? Number(quantity) : 1;
    const staticUnitPrice = parseFloat(selectedOption.dataset.dprice || 0);
    const fallback = { price: this.calculateAddonPrice(staticUnitPrice, qty), unitPrice: staticUnitPrice, currency: null, handle: null, variantId: null };

    const selectedFinish = document.querySelector('input[name="finish"]:checked');
    const finishCode = selectedFinish ? String(selectedFinish.value || "").trim() : "";

    const fetchProductByHandle = async (handle) => {
      try {
        const data = await gql(Queries.resolvedSkuProduct, { handle });
        return data?.product || null;
      } catch (error) {
        console.error("[AddonDropdowns] bundle price lookup failed:", error.message);
        return null;
      }
    };

    const baseCode = selectedOption.getAttribute("data-dcode");

    // ── Non-bundle option (has rosset_code / data-dcode) ──────────────────
    if (baseCode) {
      let handle = finishCode ? `${baseCode}${finishCode}` : "";
      let product = handle ? await fetchProductByHandle(handle) : null;
      if (!product) {
        // Fallback: PSS→PC, SSS→SN, else→SS
        handle = `${baseCode}${sparePartFallbackFinish(finishCode)}`;
        product = await fetchProductByHandle(handle);
      }
      if (!product) return fallback;
      const unitPrice = parseFloat(product.priceRange?.minVariantPrice?.amount || 0);
      const currency = product.priceRange?.minVariantPrice?.currencyCode || null;
      const resolvedVariantId = product.variants?.edges?.[0]?.node?.id
        ? Utils.gidToId(product.variants.edges[0].node.id) : null;
      return { price: this.calculateAddonPrice(unitPrice, qty), unitPrice, currency, handle, variantId: resolvedVariantId, name: baseCode };
    }

    // ── Native Bundle option (no data-dcode): fetch all components ─────────
    const bundleHandle = selectedOption.getAttribute("data-dhandle");
    if (!bundleHandle) return fallback;

    let components = [];
    try {
      const data = await gql(Queries.bundleComponentProduct, { handle: bundleHandle });
      components = data?.product?.selectedOrFirstAvailableVariant?.components?.nodes || [];
    } catch (error) {
      console.error("[AddonDropdowns] bundle component lookup failed:", error.message);
    }

    if (!components.length) return fallback;

    let totalPrice = 0;
    let firstUnitPrice = null;
    let firstCurrency = null;
    let firstHandle = null;
    let firstVariantId = null;
    let firstName = null;

    for (const component of components) {
      const componentTitle = component?.productVariant?.product?.title;
      const componentQty = Number(component?.quantity) > 0 ? Number(component.quantity) : 1;
      const componentPriceAmount = component?.productVariant?.price?.amount;
      const componentCurrency = component?.productVariant?.price?.currencyCode || null;
      const componentVariantIdRaw = component?.productVariant?.id;
      const componentVariantId = componentVariantIdRaw ? Utils.gidToId(componentVariantIdRaw) : null;

      let unitPrice = null;
      let resolvedHandle = null;
      let resolvedVariantId = null;
      let resolvedCurrency = componentCurrency;

      if (componentTitle) {
        // 1. Try same-finish SKU
        let skuProduct = null;
        if (finishCode) {
          skuProduct = await fetchProductByHandle(`${componentTitle}${finishCode}`);
          if (skuProduct) resolvedHandle = `${componentTitle}${finishCode}`;
        }
        // 2. Fallback: PSS→PC, SSS→SN, else→SS
        if (!skuProduct) {
          const fallbackFinish = sparePartFallbackFinish(finishCode);
          skuProduct = await fetchProductByHandle(`${componentTitle}${fallbackFinish}`);
          if (skuProduct) resolvedHandle = `${componentTitle}${fallbackFinish}`;
        }
        if (skuProduct) {
          unitPrice = parseFloat(skuProduct.priceRange?.minVariantPrice?.amount || 0);
          resolvedCurrency = skuProduct.priceRange?.minVariantPrice?.currencyCode || componentCurrency;
          const varNode = skuProduct.variants?.edges?.[0]?.node;
          if (varNode?.id) resolvedVariantId = Utils.gidToId(varNode.id);
        }
      }

      // 3. Fallback to component's own direct price
      if (unitPrice === null && componentPriceAmount != null) {
        unitPrice = parseFloat(componentPriceAmount);
        resolvedVariantId = resolvedVariantId || componentVariantId;
      }

      if (unitPrice === null) unitPrice = 0;

      totalPrice += this.calculateAddonPrice(unitPrice, componentQty * qty);

      if (firstUnitPrice === null) {
        firstUnitPrice = unitPrice;
        firstCurrency = resolvedCurrency;
        firstHandle = resolvedHandle || (componentTitle ? `${componentTitle}${sparePartFallbackFinish(finishCode)}` : null);
        firstVariantId = resolvedVariantId;
        firstName = componentTitle || null;
      }
    }

    return {
      price: totalPrice,
      unitPrice: firstUnitPrice || 0,
      currency: firstCurrency,
      handle: firstHandle,
      variantId: firstVariantId,
      name: firstName,
    };
  },

  /**
   * Keep hidden input properties[Dropdown Product] in sync on the product
   * form for Cart.
   *
   * Always resolves to the dropdown option's own full descriptive title
   * (e.g. "Timber/Metal Doors min. 10mm thick - 2 x SC143 Washer & CSK
   * bolt SC81") unless data-dcode is set directly on a non-bundle SKU
   * option. Do NOT pass the short base code resolved by
   * resolveBundleItemPrice (e.g. "SC143") here — that code is only meant
   * for internal SKU/price lookups, not for what the shopper sees in
   * cart/checkout.
   * @param {string} [overrideName] - reserved for cases where the caller
   *   explicitly wants to override the visible cart property text.
   */
  updateFormDropdownProperty(overrideName) {
    const selectedOption = this._findActiveSelection();
    const forms = document.querySelectorAll('.product-form form, form[action*="/cart/add"], form.product-form');
    forms.forEach((form) => {
      let propInput = form.querySelector('input[name="properties[Dropdown Product]"]');
      if (selectedOption && selectedOption.value && !selectedOption.textContent.toLowerCase().includes("select ")) {
        if (!propInput) {
          propInput = document.createElement("input");
          propInput.type = "hidden";
          propInput.name = "properties[Dropdown Product]";
          form.appendChild(propInput);
        }
        // Prefer (in order): the async-resolved actual product name, the
        // option's own data-dcode (set directly for non-bundle SKU
        // options), then fall back to the raw dropdown text.
        propInput.value = overrideName || selectedOption.getAttribute("data-dcode") || selectedOption.textContent.trim();
      } else if (propInput) {
        propInput.remove();
      }
    });
  },

  _renderCartNotification(product) {
    if (!DOM.cartNotificationContainer) return;

    setTimeout(() => {
      DOM.cartNotificationContainer.innerHTML = `
        <div id="cart-notification-product" class="cart-notification-product">
          <div class="cart-item__image-container gradient global-media-settings">
            <a href="${product.url}" class="cart-item__link" aria-hidden="true" tabindex="-1">
              <img src="${product.image || Config.PLACEHOLDER_IMAGE_URL}"
                   class="cart-item__image" alt="${product.product_title}"
                   loading="lazy" width="70" height="70">
            </a>
          </div>
          <div>
            <a href="${product.url}"><h3 class="cart-notification-product__name h4">${product.product_title}</h3></a>
            <dl></dl>
          </div>
        </div>
      `;

      // Bump the header cart-count bubble.
      const bubble = document.querySelector(".cart-count-bubble span");
      if (bubble) {
        const current = parseInt(bubble.textContent.trim(), 10);
        bubble.textContent = isNaN(current) ? 1 : current + 1;
      }

      // Bump the "View Cart (N)" button count on the notification.
      const viewCartBtn = document.getElementById("cart-notification-button");
      if (viewCartBtn) {
        viewCartBtn.innerHTML = viewCartBtn.innerHTML.replace(/\((\d+)\)/, (_, n) => `(${parseInt(n, 10) + 1})`);
      }
    }, 200);
  },
};


/* ============================================================================
 * 12. ADD-TO-CART BUTTON
 * ========================================================================= */

const CartButton = {
  enableForProduct(product) {
    const btn = DOM.addToCartButton;
    if (!btn) return;

    btn.disabled = false;
    btn.innerHTML = "Add to Cart";
    btn.setAttribute("data-productid", Utils.gidToId(product.id));
    btn.setAttribute("data-id", Utils.gidToId(product.variants.edges[0].node.id));

    // Keep the hidden <form> in sync so the theme's cart submit picks up the SKU.
    if (DOM.productForm) {
      const idInput = DOM.productForm.querySelector('input[name="id"]');
      const productIdInput = DOM.productForm.querySelector('input[name="product-id"]');
      if (idInput) idInput.value = Utils.gidToId(product.variants.edges[0].node.id);
      if (productIdInput) productIdInput.value = Utils.gidToId(product.id);
    }
  },

  disable(label = "Add to Cart") {
    const btn = DOM.addToCartButton;
    if (!btn) return;
    btn.disabled = true;
    btn.innerHTML = label;
  },

  /**
   * Wire up the click handler. Clicking our custom button submits the cart form
   * containing the main product and optional add-on product in a single Add to Cart action.
   */

  bindClick() {
    if (!DOM.addToCartButton) return;

    DOM.addToCartButton.addEventListener("click", async () => {
      if (DOM.cartNotificationContainer) {
        DOM.cartNotificationContainer.innerHTML = "";
      }

      // Guard against double-clicks while the sequence below is in flight —
      // without this, a second click could still overlap the first click's
      // requests and reintroduce the same cart race.
      if (DOM.addToCartButton.disabled) return;
      const originalLabel = DOM.addToCartButton.innerHTML;
      DOM.addToCartButton.disabled = true;

      try {
        // Check if an add-on product is selected from dropdowns
        const selectedAddonOption = AddonDropdowns._findActiveSelection();
        const addonVariantId = selectedAddonOption?.getAttribute("data-variant-id");

        if (addonVariantId) {
          const groupId = 'g_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
          AddonDropdowns._activeGroupId = groupId;

          const forms = document.querySelectorAll('.product-form form, form[action*="/cart/add"], form.product-form');
          forms.forEach((form) => {
            let groupInput = form.querySelector('input[name="properties[_group_id]"]');
            if (!groupInput) {
              groupInput = document.createElement("input");
              groupInput.type = "hidden";
              groupInput.name = "properties[_group_id]";
              form.appendChild(groupInput);
            }
            groupInput.value = groupId;
          });

          // AWAIT this fully before touching the main product's cart/add call.
          // Shopify's cart is a single read-modify-write resource per session,
          // so firing two /cart/add.js POSTs at the same instant can race and
          // silently drop one of the two items even though both requests
          // individually report success. Sequencing them removes the race.
          await AddonDropdowns.addSelectedToCart();
        } else {
          AddonDropdowns._activeGroupId = null;
          const forms = document.querySelectorAll('.product-form form, form[action*="/cart/add"], form.product-form');
          forms.forEach((form) => {
            const groupInput = form.querySelector('input[name="properties[_group_id]"]');
            if (groupInput) groupInput.remove();
          });
        }

        // Trigger standard theme Add to Cart submit for main product
        const themeAddBtn = DOM.productForm?.querySelector('[name="add"]');
        if (themeAddBtn) {
          themeAddBtn.removeAttribute("disabled");
          themeAddBtn.click();
        }
      } finally {
        AddonDropdowns._activeGroupId = null;
        DOM.addToCartButton.disabled = false;
        DOM.addToCartButton.innerHTML = originalLabel;
      }
    });
  },
};


/* ============================================================================
 * 13. PRODUCT VIEW (Content Swap)
 * ----------------------------------------------------------------------------
 * Pushes the resolved SKU product's data into every relevant slot on the PDP.
 * Each "update…" method is independent so changes to one block don't leak.
 * ========================================================================= */

const ProductView = {
  async render(product) {
    this._updateSku(product);
    this._updateShortDescription(product);
    this._updateLongDescription(product);
    this._updateFaq(product);
    this._updateInstallation(product);
    this._updateTechnicalSpec(product);
    this._updateGuaranteeReturns(product);
    this._updateDelivery(product);
    this._updateTitle(product);
    this._updatePrice(product);
    this._updateShareModal(product);
    this._updateSpecbookButtons(product); // Update attributes FIRST
    this._updateWishlistButtons(product); // Then refresh heart icon
    this._updateAddonDropdownPricing(product);
    this._updateContactUsLink(product);

    await this._updateSampleLink(product);
    await Gallery.renderFromProduct(product);

    this._resetAddonDropdowns();
    this._preloadByTypeMetaobjects(product);

    ContentSwap.toResolved();
  },

  _updateContactUsLink(product) {
    document.querySelectorAll(".sp_find_dealers a").forEach((contactBtn) => {
      const href = contactBtn.getAttribute("href") || contactBtn.getAttribute("data-href-orignal");
      if (!href) return;
      try {
        const url = new URL(href, window.location.origin);
        const sku = product.variants.edges[0].node.sku;
        const title = product.title;

        url.searchParams.set("product_sku", sku);
        url.searchParams.set("product_name", title);

        const productUrl = window.location.origin + window.location.pathname + "?sku=" + sku;

        url.searchParams.set("product", productUrl);
        url.searchParams.set("product_url", productUrl);

        contactBtn.setAttribute("href", url.pathname + url.search);
      } catch (e) {
        console.error("Error updating contact-us link:", e);
      }
    });
  },

  _getMeta(product, key) {
    return product.metafields?.find((m) => m && m.key === key)?.value || "";
  },

  _updateSku(product) {
    const el = document.querySelector(".js__sku");
    const sku2El = document.getElementById("js__sku2");
    const origSkuEl = document.querySelector(".js_orignal_sku");
    const sku = product?.variants?.edges?.[0]?.node?.sku || "";

    if (sku2El) sku2El.value = sku;
    if (el) {
      el.innerText = sku ? "Product Code: " + sku : "";
      el.classList.toggle("d-none", !sku);
    }
    if (origSkuEl) {
      origSkuEl.classList.toggle("d-none", !!sku);
    }
  },

  _clearSku() {
    const el = document.querySelector(".js__sku");
    const sku2El = document.getElementById("js__sku2");
    const origSkuEl = document.querySelector(".js_orignal_sku");

    if (sku2El) sku2El.value = "";
    if (el) {
      el.innerText = "";
    }
    if (origSkuEl) {
      origSkuEl.classList.remove("d-none");
    }
  },

  _updateShortDescription(product) {
    const el = document.querySelector(".js_short_description");
    if (el) el.innerHTML = this._getMeta(product, "short_description");
  },

  _updateLongDescription(product) {
    const el = document.querySelector(".js_description");
    if (el) el.innerHTML = product.descriptionHtml;
  },

  _updateWishlistButtons(product) {
    const epi = Utils.gidToId(product.variants.edges[0].node.id);
    const empi = Utils.gidToId(product.id);
    const du = document.location.origin + window.Shopify?.routes?.root + "products/" + product.handle;
    if (epi && empi && du) Wishlist.refreshButtonState(epi, empi, du);
  },

  _updateFaq(product) {
    const html = this._getMeta(product, "faq").replace(/\n\n/g, "<br>");
    document.querySelectorAll(".js_faq").forEach((el) => (el.innerHTML = html));
  },

  _updateInstallation(product) {
    const html = this._getMeta(product, "installation");
    document.querySelectorAll(".js_installation").forEach((el) => (el.innerHTML = html));
  },

  _updateTechnicalSpec(product) {
    const html = this._getMeta(product, "technical_specification");
    document.querySelectorAll(".js_technical_specification").forEach((el) => (el.innerHTML = html));
  },

  _updateGuaranteeReturns(product) {
    // Market-specific copy — each region has its own metafield.
    const keyByMarket = {
      AU: "guarantee_returns",
      US: "guarantee_returns_usa",
      GB: "guarantee_returns_gbp",
    };
    const key = keyByMarket[country_code] || "guarantee_returns";
    const html = this._getMeta(product, key);
    document.querySelectorAll(".js_guarantee_returns").forEach((el) => {
      let content = html;
      if (country_code === "US") {
        const shopUsaGuarantee = el.getAttribute("data-shop-usa-guarantee") || document.querySelector("[data-shop-usa-guarantee]")?.getAttribute("data-shop-usa-guarantee");
        if (shopUsaGuarantee) {
          content = shopUsaGuarantee;
        }
      }
      el.innerHTML = content;
    });
  },

  _updateDelivery(product) {
    const value = this._getMeta(product, "estimated_delivery");
    document.querySelectorAll(".js__delivery").forEach((el) => {
      const p = el.querySelector("p");
      if (p) p.innerHTML = value === "" ? "" : "Estimated Dispatch: " + value;
    });
  },

  async _updateSampleLink(product) {
    const sampleEl = document.querySelector(".js_request_sample");
    if (!sampleEl) return;

    const sampleId = this._getMeta(product, "link_sample_product");
    if (!sampleId) {
      sampleEl.classList.add("d-none");
      sampleEl.querySelector("p").innerHTML = "";
      return;
    }

    sampleEl.querySelector("p").innerHTML = "";
    try {
      const data = await gql(Queries.sampleProductById, { id: sampleId });
      const sampleProduct = data?.product;
      if (sampleProduct) {
        sampleEl.classList.remove("d-none");
        sampleEl.querySelector("p").innerHTML =
          `<a href="${window.Shopify?.routes?.root}products/${sampleProduct.handle}">Request a sample</a>`;
      } else {
        sampleEl.classList.add("d-none");
        sampleEl.querySelector("p").innerHTML = "";
      }
    } catch (error) {
      console.error("[ProductView._updateSampleLink] failed:", error.message);
    }
  },

  _updateTitle(product) {
    document.querySelectorAll(".js_sku_product_title").forEach((el) => {
      const p = el.querySelector("p");
      if (p) p.innerHTML = product.title;
    });
  },

  _updatePrice(product) {
    const priceEl = document.querySelector(".js_price");
    if (priceEl) priceEl.innerHTML = PriceHTML.build(product);

    // Re-init Bootstrap tooltips inside the price block (fresh DOM → new instances needed).
    const triggers = [].slice.call(document.querySelectorAll('.single_product_price [data-bs-toggle="tooltip"]'));
    triggers.forEach((el) => new bootstrap.Tooltip(el));
  },

  _updateShareModal(product) {
    if (!DOM.cartProductShare) return;
    const url = document.location.origin + window.Shopify?.routes?.root + "products/" + product.handle;
    DOM.cartProductShare.querySelectorAll("a").forEach((el) => {
      el.setAttribute("href", el.getAttribute("data-prefix") + url);
    });

    const linkInput = document.querySelector(".share-link-input");
    // NOTE: legacy behaviour referenced `newPara` / `ProductCode` here that isn't
    // defined in this scope. Falling back to the product handle URL — if your
    // share link truly needs query params (?backplate=…&finish=…), wire them in
    // here from State.selectedValues.
    if (linkInput) linkInput.value = url;
  },

  _updateSpecbookButtons(product) {
    const epi = Utils.gidToId(product.variants.edges[0].node.id);
    const empi = Utils.gidToId(product.id);
    const du = document.location.origin + window.Shopify?.routes?.root + "products/" + product.handle;

    // Get finish code, name, and image from the active configurator state.
    // Fall back to first visible swatch if nothing is explicitly checked.
    const activeFinish = document.querySelector('input[name="finish"]:checked')
      || document.querySelector('.accordion_varient:not([style*="display: none"]) input[name="finish"]')
      || document.querySelector('input[name="finish"]');
    const fc = activeFinish ? activeFinish.value : "";
    const fn = activeFinish ? activeFinish.getAttribute("title") : "";
    const image = activeFinish ? activeFinish.getAttribute("data-variant-image") : "";
    const title = product.title + (fn ? " - " + fn : "");

    document.querySelectorAll(".js_specbook_button").forEach((el) => {
      // Avoid overwriting buttons inside related product cards on the PDP
      if (el.closest('.card_product_block')) return;

      el.setAttribute("data-empi", empi);
      el.setAttribute("data-epi", epi);
      el.setAttribute("data-du", du);
      el.setAttribute("data-wlh-image", image);
      el.setAttribute("data-wlh-name", title);
      el.setAttribute("data-wlh-fc", fc);
      el.setAttribute("data-wlh-fn", fn);
    });

    document.querySelectorAll(".js_specbook_download_button").forEach((el) => {
      el.setAttribute("data-empi", empi);
      el.setAttribute("data-epi", epi);
      el.setAttribute("data-du", du);
      el.setAttribute("data-sku", product.variants.edges[0].node.sku);
      el.setAttribute("data-handle", product.handle);
      el.setAttribute("data-title", product.title);
      el.setAttribute("data-description", product.title);
      if (product.images.edges.length > 0) {
        el.setAttribute("data-image", product.images.edges[0].node.url);
      }
    });
  },

  _updateAddonDropdownPricing(product) {
    // When the user picks an add-on from a dropdown, reflect that extra cost
    // in the displayed price of the MAIN product. Base = this SKU's price.
    const dropdowns = document.querySelectorAll(".form-select-dropdown-product");
    let basePrice = parseFloat(product.priceRange.minVariantPrice.amount);
    const currency = product.priceRange.minVariantPrice.currencyCode;

    if (basePrice <= 0) {
      const priceEl = document.querySelector(".js_orignal_price .price") || document.querySelector(".price");
      const mainPrice = priceEl ? parseFloat(priceEl.dataset.mainPrice || 0) : 0;
      if (mainPrice > 0) {
        basePrice = mainPrice;
      }
    }

    dropdowns.forEach((dropdown) => {
      dropdown.addEventListener("change", async () => {
        const selected = dropdown.options[dropdown.selectedIndex];
        const priceEl = document.querySelector(".js_price .price-item--regular");
        const qtyInput = document.querySelector('.quantity__input[name="quantity"], [name="quantity"]');
        const quantity = parseFloat(qtyInput?.value) || 1;

        // Instant feedback with the option's own baked-in price while the
        // finish-aware lookup resolves below, so the price never sits blank.
        const instantExtra = parseFloat(selected.dataset.dprice || 0) * quantity;
        if (priceEl) priceEl.textContent = `${getCurrencySymbol(currency)}${(basePrice + instantExtra).toFixed(2)} ${currency}`;

        const resolved = await AddonDropdowns.resolveBundleItemPrice(selected, quantity);
        const extra = resolved ? resolved.price : instantExtra;
        const total = (basePrice + extra).toFixed(2);
        if (priceEl) priceEl.textContent = `${getCurrencySymbol(resolved?.currency || currency)}${total} ${resolved?.currency || currency}`;

        // Always use the dropdown option's own full descriptive title (e.g.
        // "Timber/Metal Doors min. 10mm thick - 2 x SC143 Washer & CSK bolt
        // SC81") for the cart's "Dropdown Product" line item property — the
        // short base code resolved by resolveBundleItemPrice (e.g. "SC143")
        // is only used internally to look up the SKU/price and should not
        // replace what the shopper sees in cart/checkout.
        AddonDropdowns.updateFormDropdownProperty();
      });
    });
  },

  _resetAddonDropdowns() {
    ADDON_DROPDOWNS.forEach((dd) => {
      if (!dd) return;
      // Re-enable anything previously disabled and reset to the "choose one" option.
      [...dd.options].forEach((opt) => opt.removeAttribute("disabled"));
      if (dd.options.length > 0) dd.selectedIndex = 0;
    });
  },

  _preloadByTypeMetaobjects(product) {
    const raw = this._getMeta(product, "by_type_code");
    if (!raw) return;
    try {
      const ids = JSON.parse(raw);
      fetchMetaobjects(ids).then((nodes) => {
        const names = nodes.map((n) => n.fields.find((f) => f.key === "name")?.value).filter(Boolean);
        // Currently only logged — keep for future UI ("This works with: …").
        console.log("[ProductView] by_type names:", names);
      });
    } catch (err) {
      console.warn("[ProductView] invalid by_type_code JSON:", err);
    }
  },
};


function getCurrencySymbol(currencyCode) {
  const codeMap = {
    GBP: "£",
    USD: "$",
    AUD: "$",
  };
  if (currencyCode && codeMap[currencyCode.toUpperCase()]) {
    return codeMap[currencyCode.toUpperCase()];
  }
  if (!currencyCode) return "" || "$";

  try {
    const parts = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    const symbolPart = parts.find((p) => p.type === "currency");
    if (symbolPart && symbolPart.value) {
      return symbolPart.value.replace(/^[A-Z]+/, "");
    }
  } catch (e) { }

  return "" || "$";
}

const PriceHTML = {
  build(product) {
    let regAmount = parseFloat(product.priceRange.minVariantPrice.amount);
    let cmpAmount = parseFloat(product.compareAtPriceRange.minVariantPrice.amount);
    const currency = product.priceRange.minVariantPrice.currencyCode;
    const symbol = getCurrencySymbol(currency);

    if (regAmount <= 0) {
      const priceEl = document.querySelector(".js_orignal_price .price") || document.querySelector(".price");
      const basePrice = priceEl ? parseFloat(priceEl.dataset.mainPrice || 0) : 0;
      if (basePrice > 0) {
        regAmount = basePrice;
        if (!(cmpAmount > 0)) {
          cmpAmount = basePrice;
        }
      }
    }

    const onSale = cmpAmount > regAmount;
    const regAmountStr = regAmount.toFixed(2);
    const cmpAmountStr = cmpAmount.toFixed(2);

    // Real tooltip copy comes from the section setting (section.settings.price_tooltip),
    // exposed by price.liquid as window.priceTooltipText. Fall back to whatever is
    // currently in the DOM (first server render) so we never show placeholder text.
    const existingTooltipEl = document.querySelector(".single_product_price [data-bs-toggle=\"tooltip\"]");
    const tooltipText =
      window.priceTooltipText ||
      existingTooltipEl?.getAttribute("data-bs-title") ||
      "";
    const tooltipTextEscaped = tooltipText
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    return ""; // to comment PRICE, as US launch 
    return `<h6>Price</h6>
      <div class="sp_price_wrapper d-flex align-items-sm-end align-items-start">
        <div class="price ${onSale ? "price--on-sale " : ""}price--large price--show-badge" data-main-price="${regAmountStr}" data-compare-price="${onSale ? cmpAmountStr : ""}" data-currency="${currency}">
          <div class="price__container">
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular">${symbol}${regAmountStr} ${currency}</span>
            </div>
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span><s class="price-item price-item--regular">${symbol}${cmpAmountStr} ${currency}</s></span>
              <span class="visually-hidden visually-hidden--inline">Sale price</span>
              <span class="price-item price-item--sale price-item--last">${symbol}${regAmountStr} ${currency}</span>
            </div>
            <small class="unit-price caption hidden">
              <span class="visually-hidden">Unit price</span>
              <span class="price-item price-item--last">
                <span></span><span aria-hidden="true">/</span>
                <span class="visually-hidden">&nbsp;per&nbsp;</span><span></span>
              </span>
            </small>
          </div>
          <div class="d-flex flex-column">
            <div class="product_badges_wrapper"></div>
            <a type="button" data-bs-toggle="tooltip" data-bs-custom-class="common_cart_box"
               data-bs-placement="top"
               data-bs-title="${tooltipTextEscaped}"
               class="common_info_tooltip product_info_tag d-inline-block"${tooltipTextEscaped ? "" : " style=\"display:none;\""}>
              <img src="https://cdn.shopify.com/s/files/1/0881/1662/5712/files/info-icon.png?v=1732077528"
                   loading="lazy" width="auto" height="auto" alt="More Options">
            </a>
          </div>
        </div>
      </div>`;
  },
};


/* ============================================================================
 * 15. CONTENT SWAP
 * ----------------------------------------------------------------------------
 * Flip between main-product content (while user is mid-configuration) and
 * resolved-SKU content (once a full combo is picked).
 * ========================================================================= */

const ContentSwap_helpers = {
  apply(showOriginal) {
    // Dismiss any open tooltips — they'll leak outside their element when we swap.
    document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
      bootstrap.Tooltip.getInstance(el)?.hide();
    });

    ContentSwap.original.forEach((origClass, i) => {
      const newClass = ContentSwap.new[i];
      const origEls = document.querySelectorAll(`.${origClass}`);
      const newEls = document.querySelectorAll(`.${newClass}`);

      // Delivery has a special rule: only show the SKU-specific copy if the
      // SKU actually returned delivery text (otherwise keep Main Product copy).
      if (newClass === "js__delivery") {
        newEls.forEach((newEl, idx) => {
          const pTag = newEl.querySelector("p");
          const hasContent = pTag && pTag.textContent.trim() !== "";

          // Default: show original, hide new.
          origEls.forEach((el) => el.classList.remove("d-none"));
          newEls[idx].classList.add("d-none");

          if (hasContent) {
            origEls.forEach((el) => el.classList.add("d-none"));
            newEls[idx].classList.remove("d-none");
          }
        });
      } else {
        origEls.forEach((el) => el.classList.toggle("d-none", !showOriginal));
        newEls.forEach((el) => el.classList.toggle("d-none", showOriginal));
      }
    });

    ShareAndSpecbook.restoreToMainIfNeeded(showOriginal);
    if (showOriginal) {
      if (Object.keys(State.selectedValues).length === 0) {
        Gallery.reset();
      } else if (State.skuCandidates.length > 0 && !Gallery.shouldPreserveUrlFinishImage()) {
        Gallery.updateFromCurrentSelection();
      }
    }
  },
};

ContentSwap.toOriginal = () => ContentSwap_helpers.apply(true);
ContentSwap.toResolved = () => ContentSwap_helpers.apply(false);


/* ============================================================================
 * 16. SHARE MODAL + SPECBOOK RESTORATION
 * ----------------------------------------------------------------------------
 * When reverting to Main Product view we need to restore the original hrefs /
 * data attrs that were set at page load (stored in data-*-orignal [sic]).
 * ========================================================================= */

const ShareAndSpecbook = {
  restoreToMainIfNeeded(showOriginal) {
    if (!showOriginal) return;

    // Share modal
    if (DOM.cartProductShare) {
      DOM.cartProductShare.querySelectorAll("a").forEach((el) => {
        el.setAttribute("href", el.getAttribute("data-href"));
      });
      const linkInput = document.querySelector(".share-link-input");
      if (linkInput) linkInput.value = linkInput.getAttribute("data-value");
    }

    // Specbook buttons
    const restoreAttrs = (el, keys) => {
      keys.forEach((k) => el.setAttribute(`data-${k}`, el.getAttribute(`data-${k}-orignal`)));
    };
    document.querySelectorAll(".js_specbook_button").forEach((el) => {
      restoreAttrs(el, ["empi", "epi", "du"]);
    });
    document.querySelectorAll(".js_specbook_download_button").forEach((el) => {
      restoreAttrs(el, ["empi", "epi", "du", "sku", "handle", "title"]);
    });

    // Contact Us buttons
    document.querySelectorAll(".sp_find_dealers a").forEach((el) => {
      const orig = el.getAttribute("data-href-orignal");
      if (orig) el.setAttribute("href", orig);
    });
  },
};

/* ============================================================================
 * 17. GALLERY (Flicker-Free Async Pre-Decoded Version)
 * ========================================================================= */

const Gallery = {
  _initialSkeletonApplied: false,
  _skeletonSafetyTimer: null,

  /**
   * Covers the server-rendered MAIN product image(s) with the same skeleton
   * placeholder used elsewhere (card-product.liquid) the instant we know this
   * page load is going to resolve a specific SKU (?sku= present in the URL).
   *
   * Without this, the shopper sees the generic MAIN product image for the
   * ~2-3s it takes fromSku() → applyRestAfterFetch() → resolveSkuProduct() to
   * fetch and swap in the correct SKU image — which reads as "wrong image,
   * then it corrects itself." Call this as early as possible in bootstrap,
   * before any of that async work kicks off.
   *
   * Safe no-op if there's no ?sku= param, or if slides/images aren't present.
   */
  showInitialSpinner() {
    const params = Utils.getSearchParams();
    if (!params.get("sku")) return;

    const cover = (slide) => {
      const img = slide.querySelector("img");
      if (!img || !img.parentElement || slide.querySelector(".sku-initial-spinner")) return;
      const spinner = document.createElement("div");
      spinner.className = "loading__spinner sku-initial-spinner";
      spinner.innerHTML = `
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" width="30px" height="30px" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      `;
      img.parentElement.insertBefore(spinner, img);
      img.style.opacity = "0";
    };

    const slides = document.querySelectorAll(
      ".main_product_slider .swiper-slide:not(.prependSlide), .thumb_product_slider .swiper-slide:not(.prependSlide)"
    );
    slides.forEach(cover);
    this._initialSkeletonApplied = slides.length > 0;

    // Safety net: if for any reason none of the normal completion paths fire
    // (e.g. the SKU doesn't match this product's code and fromSku() aborts
    // before ever reaching resolveSkuProduct()), don't leave the shopper
    // staring at a loading overlay forever — reveal the original image.
    if (this._initialSkeletonApplied) {
      clearTimeout(this._skeletonSafetyTimer);
      this._skeletonSafetyTimer = setTimeout(() => this.clearInitialSkeleton(), 8000);
    }
  },

  showInitialSkeleton() {
    this.showInitialSpinner();
  },

  /** Remove the initial spinner cover (if present) and restore visibility of
   *  whichever image ended up underneath. Idempotent — safe to call from
   *  multiple completion paths (success, not-found, error, timeout). */
  clearInitialSkeleton() {
    clearTimeout(this._skeletonSafetyTimer);
    if (!this._initialSkeletonApplied) return;
    document.querySelectorAll(".sku-initial-spinner, .sku-initial-skeleton").forEach((el) => el.remove());
    document
      .querySelectorAll(".main_product_slider .swiper-slide img, .thumb_product_slider .swiper-slide img")
      .forEach((img) => { img.style.opacity = ""; });
    this._initialSkeletonApplied = false;
  },

  shouldPreserveUrlFinishImage() {
    const finishParam = Utils.getSearchParams().get("finish");
    if (!finishParam) return false;

    const allOptionsChosen = DOM.customVariants.length === Object.keys(State.selectedValues).length;
    if (allOptionsChosen) return false;

    const normalize = (value) => String(value || "").toLowerCase().replace(/^-+/, "");
    const targetFinish = normalize(finishParam);
    if (!targetFinish) return false;

    const finishInputs = document.querySelectorAll('input[name="finish"]');
    for (const input of finishInputs) {
      const inputFinish = normalize(input.value || input.getAttribute("data-value"));
      if (inputFinish !== targetFinish) continue;
      return !!input.getAttribute("data-variant-image");
    }

    return false;
  },

  async updateFromCurrentSelection() {
    if (UrlPrefill._inProgress) return;
    if (this.shouldPreserveUrlFinishImage()) return;

    // ✅ FIX: The generic "Powder Coat" tile (DOM.pccVariant, the div at
    // front-product.liquid ~line 1526) doesn't map to any single finish/SKU
    // by itself — the shopper still has to pick a colour swatch inside
    // .pcc_wrapper. While that tile is open ("selected") but no ".with_pcc"
    // swatch is checked yet, PccFlow.bind() has already unchecked every
    // "finish" radio, so State.selectedValues simply has no `finish` entry.
    // Left unguarded, the `.every()` match below silently ignores that
    // missing field and matches the FIRST candidate that still satisfies
    // the other fields (Backplate/Function) — which in practice surfaces
    // whichever finish was previously resolved, so the shopper sees the
    // old finish's image instead of a placeholder. Bail out to the
    // placeholder branch explicitly whenever we're in that state.
    const pccOpenWithNoColour =
      DOM.pccVariant?.classList.contains("selected") &&
      !document.querySelector("input.with_pcc:checked");
    if (pccOpenWithNoColour) {
      if (this._initialSkeletonApplied) return;
      return this._applyImages({ metafields: [{ key: "product_media", value: `${Config.PLACEHOLDER_IMAGE_URL}` }], title: "Image not available" });
    }

    const match = State.skuCandidates.find((product) =>
      Object.entries(State.selectedValues).every(([k, v]) => {
        if (product[k] === undefined) return false;
        const prodVal = String(product[k]).toLowerCase().replace(/^-+/, "");
        const selectedVal = String(v).toLowerCase().replace(/^-+/, "");
        return prodVal === selectedVal;
      })
    );
    if (match) return this.updateFromCandidate(match);

    // No candidate match YET. If we're still inside the initial deep-link
    // resolution window (the sku-initial-skeleton is still covering the
    // gallery while fromSku()/resolveSkuProduct() do their work), do NOT
    // swap in the generic "no image" placeholder here — that's exactly the
    // flash-then-correct-itself flicker we're trying to avoid. The match
    // may simply not have loaded yet (background pagination), and
    // resolveSkuProduct() resolves the real SKU product independently by
    // handle, so it will supply the real image (or genuinely confirm there
    // isn't one) shortly. Just keep the skeleton up and wait.
    if (this._initialSkeletonApplied) return;

    return this._applyImages({ metafields: [{ key: "product_media", value: `${Config.PLACEHOLDER_IMAGE_URL}` }], title: "Image not available" });
  },

  async updateFromCandidate(candidate) {
    if (UrlPrefill._inProgress) return;
    if (this.shouldPreserveUrlFinishImage()) return;
    if (!candidate?.id) return;

    try {
      const data = await gql(Queries.productGalleryById, { id: candidate.id });
      const product = data?.product;
      if (!product) return;
      this._applyImages(product);
    } catch (error) {
      // Keep existing images on error to avoid unnecessary visual flashing
    }
  },

  async renderFromProduct(product) {
    this._applyImages(product);
  },

  /** Helper: Pre-loads & decodes images in memory before DOM injection */
  _preloadImages(urls) {
    return Promise.all(
      urls.map((url) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = url;
          if (img.complete) {
            resolve(url);
          } else {
            img.onload = () => {
              if ("decode" in img) {
                img.decode().then(() => resolve(url)).catch(() => resolve(url));
              } else {
                resolve(url);
              }
            };
            img.onerror = () => resolve(url); // Proceed even if an individual image fails
          }
        });
      })
    );
  },

  async _applyImages(product) {
    // NOTE: we intentionally do NOT clear the initial loading skeleton here.
    // Doing so immediately reveals the stale/default image underneath before
    // the correct SKU image has even started loading, which is exactly the
    // "placeholder flashes first" flicker we want to avoid. Instead, the
    // skeleton stays up until we know what's about to replace it, and we
    // clear it at each exit point below, right as the final content lands.

    const mediaMeta = product.metafields?.find((m) => m && m.key === "product_media");
    let urls;
    if (mediaMeta?.value) {
      urls = mediaMeta.value.split(",").map((s) => s.trim()).filter(Boolean);
    } else if (product.images?.edges?.length > 0) {
      urls = product.images.edges.map((e) => e?.node?.url).filter(Boolean);
    } else {
      urls = [];
    }

    // Dynamic SKU Image Fallback
    if (urls.length === 0) {
      const sku = product.variants?.edges?.[0]?.node?.sku;
      if (sku) {
        let originalSrc = "";
        const originalMainImg = document.querySelector(".main_product_slider .swiper-slide:not(.prependSlide) img");
        if (originalMainImg) {
          originalSrc = originalMainImg.getAttribute("src") || originalMainImg.src || "";
        }
        if (!originalSrc) {
          const anyImg = document.querySelector(".main_product_slider img");
          if (anyImg) originalSrc = anyImg.getAttribute("src") || anyImg.src || "";
        }
        if (originalSrc && originalSrc.includes("/")) {
          const baseFolder = originalSrc.substring(0, originalSrc.lastIndexOf("/") + 1);
          urls = [`${baseFolder}${sku.toUpperCase()}.jpg`];
        }
      }
    }

    const urlsString = urls.join("|");
    if (this._lastUrls === urlsString) {
      // Nothing is actually changing, so there's nothing new to wait on —
      // release the skeleton now rather than leaving it up indefinitely.
      this.clearInitialSkeleton();
      return;
    }
    this._lastUrls = urlsString;

    if (urls.length === 0) {
      // Genuinely no image data for this SKU — reset() clears the skeleton
      // and leaves the existing placeholder image in place, as expected.
      this.reset();
      return;
    }

    // Pre-decode main image to eliminate page-load flickering. The loading
    // skeleton stays visible for the whole of this await — only once the
    // real image is decoded and ready do we touch the DOM/skeleton below.
    await this._preloadImages(urls.slice(0, 1));

    if (urls.length > 0) {
      document.querySelectorAll(".finishPrependSlide").forEach((el) => el.remove());
      window.__pdpFinishImageUrl = "";
      window.__pdpFinishImageAppliedAt = 0;
    }

    const mainWrapper = document.querySelector(".main_product_slider .swiper-wrapper");
    const thumbWrapper = document.querySelector(".thumb_product_slider .swiper-wrapper");
    const fallbackUrl = Config.PLACEHOLDER_IMAGE_URL;
    const onError = `if (window.handlePdpImageError) window.handlePdpImageError(this); else { this.onerror=null; this.src='${fallbackUrl}'; }`;
    const finishMainSlide = document.querySelector(".main_product_slider .finishPrependSlide");
    const finishThumbSlide = document.querySelector(".thumb_product_slider .finishPrependSlide");
    const existingMainSlide = document.querySelector(".main_product_slider .prependSlide");
    const existingThumbSlide = document.querySelector(".thumb_product_slider .prependSlide");

    if (this._preserveLiveFinishImage(existingMainSlide, existingThumbSlide, onError, product.title)) {
      this.clearInitialSkeleton();
      return;
    }

    if (finishMainSlide || finishThumbSlide) {
      this._cleanup();

      const promoteSlide = (slide, isMain) => {
        if (!slide) return;
        slide.classList.remove("finishPrependSlide");
        slide.classList.add("prependSlide", "Metafield_productImage");

        const img = slide.querySelector("img");
        const visibleSrc = img ? (img.currentSrc || img.src || img.getAttribute("src") || "") : "";
        const src = visibleSrc || urls[0];

        if (img) {
          img.setAttribute("onerror", onError);
          img.alt = product.title;
          if (!visibleSrc) img.src = src;
        }

        if (isMain) {
          const anchor = slide.querySelector("a[data-fancybox]");
          if (anchor) anchor.href = src;
        }
      };

      promoteSlide(finishMainSlide, true);
      promoteSlide(finishThumbSlide, false);
      this.reinit();
      if (typeof galleryMain !== "undefined" && galleryMain) galleryMain.slideTo(0, 0);
      this.clearInitialSkeleton();
      return;
    }

    this._cleanup();

    // Dynamic image binder: reveals image once loaded and hides spinner loader
    const bindSpinner = (img) => {
      if (!img) return;
      const spinner = img.previousElementSibling;
      const reveal = () => {
        img.style.opacity = "1";
        if (spinner && (spinner.classList.contains("sku-initial-spinner") || spinner.classList.contains("card-img-skeleton"))) {
          spinner.classList.add("hidden");
        }
      };
      if (img.complete && img.naturalWidth) { reveal(); return; }
      img.addEventListener("load", reveal, { once: true });
      img.addEventListener("error", reveal, { once: true });
    };

    const spinnerHtml = `<div class="loading__spinner sku-initial-spinner">
      <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" width="30px" height="30px" xmlns="http://www.w3.org/2000/svg">
        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
      </svg>
    </div>`;

    // Normalizes a CloudFront image filename to uppercase (SKU files are always
    // uppercase on S3 e.g. DS60-SN.jpg, but metafields may store lowercase).
    const normalizeCloudfrontUrl = (u) => {
      if (!u || !u.includes("cloudfront.net")) return u;
      const lastSlash = u.lastIndexOf("/");
      const base = u.substring(0, lastSlash + 1);
      const filename = u.substring(lastSlash + 1);
      const dotIdx = filename.lastIndexOf(".");
      if (dotIdx === -1) return u;
      return base + filename.substring(0, dotIdx).toUpperCase() + filename.substring(dotIdx).toLowerCase();
    };

    // 3-step onerror: thumbnail → original full-res → placeholder
    const buildOnError = (originalUrl, placeholderUrl) => {
      const esc = (s) => (s || "").replace(/'/g, "\\'");
      return `var ph='${esc(placeholderUrl)}',orig='${esc(originalUrl)}';
        if(orig&&this.src!==orig&&this.src!==ph){this.removeAttribute('data-original');this.src=orig;}
        else if(this.src!==ph){this.onerror=null;this.src=ph;}
        else{this.onerror=null;this.onload&&this.onload(new Event('load'));}`;
    };

    const placeholder = document.getElementById("product_card_placeholder")?.value
      || Config.PLACEHOLDER_IMAGE_URL;

    [...urls].reverse().forEach((rawUrl) => {
      const url = normalizeCloudfrontUrl(rawUrl);
      const thumbUrl = url && url.includes("cloudfront.net") && !url.includes("thumbnails/")
        ? url.replace("cloudfront.net/", "cloudfront.net/thumbnails/listing/")
        : url;

      if (mainWrapper) {
        mainWrapper.insertAdjacentHTML(
          "afterbegin",
          `<div class="swiper-slide prependSlide Metafield_productImage">
            <div class="main_product_img">
              <a href="${url}" data-fancybox="gallery">
                ${spinnerHtml}
                <img src="${url}" loading="eager" decoding="async" style="opacity:0" onerror="${buildOnError(url, placeholder)}" alt="${product.title}">
              </a>
            </div>
          </div>`
        );
        bindSpinner(mainWrapper.firstElementChild?.querySelector("img"));
      }
      if (thumbWrapper) {
        thumbWrapper.insertAdjacentHTML(
          "afterbegin",
          `<div class="swiper-slide prependSlide Metafield_productImage">
            <div class="thumb_product_img">
              ${spinnerHtml}
              <img src="${thumbUrl}" data-original="${url}" loading="eager" decoding="async" style="opacity:0" onerror="${buildOnError(url, placeholder)}" alt="${product.title}">
            </div>
          </div>`
        );
        bindSpinner(thumbWrapper.firstElementChild?.querySelector("img"));
      }
    });


    this.reinit();
    if (typeof galleryMain !== "undefined" && galleryMain) galleryMain.slideTo(0, 0);
    this.clearInitialSkeleton();
  },

  _preserveLiveFinishImage(mainSlide, thumbSlide, onError, title) {
    const appliedAt = window.__pdpFinishImageAppliedAt || 0;
    const finishUrl = window.__pdpFinishImageUrl || "";
    const isRecentFinishChange = finishUrl && Date.now() - appliedAt < 15000;
    if (!isRecentFinishChange || (!mainSlide && !thumbSlide)) return false;

    const preserveSlide = (slide, isMain) => {
      if (!slide) return;
      slide.classList.add("prependSlide", "Metafield_productImage");

      const img = slide.querySelector("img");
      if (img) {
        img.setAttribute("onerror", onError);
        img.alt = title;
        img.setAttribute("data-finish-image-source", "finish-input");
        if (!img.getAttribute("src")) img.src = finishUrl;
      }

      if (isMain) {
        const anchor = slide.querySelector("a[data-fancybox]");
        if (anchor) anchor.href = finishUrl;
      }
    };

    preserveSlide(mainSlide, true);
    preserveSlide(thumbSlide, false);
    this.reinit();
    if (typeof galleryMain !== "undefined" && galleryMain) galleryMain.slideTo(0, 0);
    return true;
  },

  reset() {
    this.clearInitialSkeleton();
    this._lastUrls = "";
    this._cleanup();
    this.reinit();
    if (typeof galleryMain !== "undefined" && galleryMain) galleryMain.slideTo(0, 0);
  },

  _cleanup() {
    document.querySelectorAll(".prependSlide").forEach((el) => el.remove());
  },

  /** Safe batch Swiper update preventing multi-frame redraw flashes */
  reinit() {
    requestAnimationFrame(() => {
      var thumbEl = document.querySelector(".thumb_product_slider");
      var mainEl = document.querySelector(".main_product_slider");
      if (!thumbEl || !mainEl) return;

      if (window.galleryThumbs && typeof window.galleryThumbs.destroy === "function") {
        window.galleryThumbs.destroy(true, true);
        window.galleryThumbs = null;
      }
      if (window.galleryMain && typeof window.galleryMain.destroy === "function") {
        window.galleryMain.destroy(true, true);
        window.galleryMain = null;
      }

      window.galleryThumbs = new Swiper(".thumb_product_slider", {
        spaceBetween: 10,
        slidesPerView: 4,
        slidesPerGroup: 1,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        slideToClickedSlide: true,
        freeMode: false,
        loop: false,
        rewind: false,
        navigation: {
          nextEl: ".thumb-swiper-next",
          prevEl: ".thumb-swiper-prev",
        },
        breakpoints: {
          0: { direction: "horizontal", slidesPerView: 4, spaceBetween: 10 },
          768: { direction: "horizontal", slidesPerView: 5, spaceBetween: 15 },
          992: { direction: "vertical", slidesPerView: 5, spaceBetween: 15 },
        },
      });

      window.galleryMain = new Swiper(".main_product_slider", {
        slidesPerView: 1,
        spaceBetween: 20,
        speed: 300,
        loop: false,
        rewind: false,
        autoHeight: false,
        navigation: { nextEl: ".swiper-button-next", prevEl: ".swiper-button-prev" },
        thumbs: { swiper: window.galleryThumbs },
        breakpoints: {
          0: { direction: "horizontal" },
          992: { direction: "vertical" },
        },
      });
    });
  },
};
window.Gallery = Gallery;

/* ============================================================================
 * 18. METAOBJECT LOOKUP (by_type_code)
 * ========================================================================= */

async function fetchMetaobjects(ids) {
  try {
    const data = await gql(Queries.filterByTypeMetaobjects, { ids });
    return data?.nodes || [];
  } catch (error) {
    console.error("[fetchMetaobjects] failed:", error);
    return [];
  }
}


/* ============================================================================
 * 19. URL PARAMETER PRE-POPULATION
 * ----------------------------------------------------------------------------
 * Example URL:
 *   /products/lc-handle?rosset=r10&finish=ss&function=psg
 * On load we replay those selections, one at a time, with a delay so each
 * underlying fetch has a chance to finish before the next kicks off.
 * ========================================================================= */

const UrlPrefill = {
  _inProgress: false,
  _params: Utils.getSearchParams(),
  _variants: [...document.querySelectorAll(".custom-variant")]
    .map((el) => el.getAttribute("data-variant-handle")),

  /**
   * When URL contains ?sku=CA-R10-SN, decompose it into per-variant params so
   * the existing applyFirstVariant / applyRestAfterFetch machinery works as-is.
   *
   * Algorithm:
   *   1. Strip the product code prefix from the SKU → suffix (e.g. "-R10-SN")
   *   2. Walk each accordion section (in DOM order) and try every radio input
   *      value against the start of the remaining suffix.
   *   3. A code matches when suffix.startsWith(code) AND the char immediately
   *      after is either "-" or end-of-string (prevents "-R1" matching "-R10").
   *   4. On match: inject into _params as data-variant-handle → code, consume
   *      the matched slice from the suffix, and move to the next section.
   */
  fromSku() {
    const skuParam = this._params.get("sku");
    if (!skuParam) return;

    const productCode = DOM.optionAccordion?.getAttribute("data-pcode") ?? "";
    console.log("[UrlPrefill.fromSku] skuParam:", skuParam, "| productCode:", productCode);
    console.log("[UrlPrefill.fromSku] variants:", this._variants);

    if (!productCode) {
      console.warn("[UrlPrefill.fromSku] No data-pcode found on .option_accordion – aborting");
      return;
    }

    if (!skuParam.toUpperCase().startsWith(productCode.toUpperCase())) {
      document.querySelectorAll(".js_specbook_button, .js_specbook_download_button").forEach((el) => {
        el.disabled = true;
        el.setAttribute("disabled", "disabled");
        el.style.pointerEvents = "none";
        el.style.opacity = "0.5";
      });
      for (let j = 1; j < DOM.customVariants.length; j++) {
        DOM.customVariants[j].querySelectorAll("input").forEach((opt) => {
          opt.parentElement.style.display = "none";
          opt.checked = false;
        });
        const pccTile = DOM.customVariants[j].querySelector(".pcc_variant");
        if (pccTile) {
          pccTile.style.display = "none";
          pccTile.classList.remove("selected");
          DOM.pccWrapper?.classList.add("d-none");
        }
      }
      console.warn("[UrlPrefill.fromSku] SKU does not start with productCode – aborting");
      return;
    }

    // Strip the product-code prefix; what remains is the options string.
    // e.g. "CL-R10-SN" with productCode "CL" → suffix "-R10-SN"
    let suffix = skuParam.substring(productCode.length);
    console.log("[UrlPrefill.fromSku] suffix after stripping product code:", suffix);
    if (!suffix) return;

    for (const variantHandle of this._variants) {
      if (!variantHandle || !suffix) continue;
      const parent = document.querySelector(`[data-variant-handle="${variantHandle}"]`);
      if (!parent) continue;

      const inputs = [...parent.querySelectorAll("input")];

      // Build a LOCAL, dash-normalized view of the suffix for comparison only.
      // We do NOT mutate the shared `suffix` here — whether THIS section's own
      // codes use a leading dash tells us nothing about whether suffix should
      // be stripped for a section that doesn't even match. Doing so previously
      // corrupted `suffix` for every later section once a dash-less section
      // (e.g. Function) was merely visited, even when it found no match.
      const hasLeadingDash = suffix.startsWith("-");
      const anyStartsWithDash = inputs.some((input) => {
        const val = input.value || input.getAttribute("data-value") || "";
        return val.startsWith("-");
      });
      const compareSuffix = (hasLeadingDash && !anyStartsWithDash) ? suffix.substring(1) : suffix;

      // Longest codes first so "-R10" is preferred over "-R1" when both exist,
      // but when only "-R1" is present it correctly consumes three chars from
      // "-R10-SN", leaving "0-SN" for the next (function) section.
      inputs.sort((a, b) => (b.value || "").length - (a.value || "").length);

      let matched = false;

      // Pass 1 (boundary-safe): only accept a code if the character immediately
      // after it is '-' or end-of-string.  This prevents "-R9" from greedily
      // consuming "-R90-FBD" when "-R90" is the real backplate code.
      console.log(`[UrlPrefill.fromSku] Processing handle="${variantHandle}", suffix="${suffix}", compareSuffix="${compareSuffix}"`);
      for (const input of inputs) {
        const code = input.value || input.getAttribute("data-value") || "";
        if (!code) continue;
        if (!compareSuffix.toUpperCase().startsWith(code.toUpperCase())) continue;
        const remaining = compareSuffix.substring(code.length);
        if (remaining === "" || remaining.startsWith("-")) {
          console.log(`[UrlPrefill.fromSku] boundary-matched handle="${variantHandle}" code="${code}", remaining="${remaining}"`);
          this._params.set(variantHandle, code);
          suffix = remaining;
          matched = true;
          break;
        }
      }

      // Pass 2 (fallback): greedy prefix match — preserves existing behaviour for
      // products where the next section's code legitimately starts mid-segment
      // (e.g. only "-R1" available and suffix is "-R10-SN", leaving "0-SN" for function).
      if (!matched) {
        for (const input of inputs) {
          const code = input.value || input.getAttribute("data-value") || "";
          if (!code) continue;
          // Strip leading dashes from BOTH sides before comparing. compareSuffix
          // may or may not still carry its own leading dash depending on the
          // anyStartsWithDash branch above — comparing a dash-stripped `code`
          // against a still-dashed `compareSuffix` (or vice versa) never
          // matches even when the underlying codes are identical, which is
          // what previously made "-R1" fail to match "-R10-SN".
          const strippedCode = code.replace(/^-+/, "");
          const strippedCompare = compareSuffix.replace(/^-+/, "");
          if (strippedCompare.toUpperCase().startsWith(strippedCode.toUpperCase())) {
            // Consume exactly as many characters from the ORIGINAL compareSuffix
            // as strippedCode represents, accounting for any dash we stripped off.
            const consumedLen = (compareSuffix.length - strippedCompare.length) + strippedCode.length;
            const remaining = compareSuffix.substring(consumedLen);
            console.log(`[UrlPrefill.fromSku] prefix-matched handle="${variantHandle}" code="${code}", remaining="${remaining}"`);
            this._params.set(variantHandle, code);
            suffix = remaining;
            matched = true;
            break;
          }
        }
      }

      if (!matched) {
        console.log(`[UrlPrefill.fromSku] no match for handle="${variantHandle}", remaining suffix="${suffix}"`);
      }
    }

    console.log("[UrlPrefill.fromSku] final _params after fromSku:", [...this._params.entries()]);
  },

  applyFirstVariant() {
    // First one fires synchronously so its fetch kicks off.
    const first = this._variants[0];

    if (!first) {
      // No variants at all — nothing to prefill.
      State.urlPrefillApplied = true;
      return;
    }

    if (!this._params.has(first)) {
      // variant[0] has no URL/SKU param (e.g. fromSku() found no matching code).
      // Do NOT set urlPrefillApplied = true here — applyRestAfterFetch() must
      // remain "armed" so that when the user manually selects the first section,
      // _doApplyRest() can still apply any later variants that DID match.
      console.warn(`[UrlPrefill.applyFirstVariant] No param for "${first}" — leaving unselected; later params still pending.`);
      return;
    }

    this._selectVariant(first, this._params.get(first));
  },

  /**
   * A section had no matching value in the ?sku= param AND has no "Blank"
   * tile of its own — meaning the choice genuinely wasn't specified but IS
   * required. Rather than leaving it visually indistinguishable from a
   * normal not-yet-reached section, mark it so the shopper notices it still
   * needs input. Idempotent — safe to call repeatedly without stacking hints.
   */
  _flagNeedsSelection(parent) {
    if (!parent || parent.classList.contains("sku-prefill-needs-selection")) return;
    parent.classList.add("sku-prefill-needs-selection");
    parent.style.outline = "2px solid #e0a800";
    parent.style.outlineOffset = "4px";
    parent.style.borderRadius = "4px";

    const hint = document.createElement("div");
    hint.className = "sku-prefill-hint";
    hint.style.cssText = "color:#8a6d00;font-size:0.85em;margin:4px 0 8px;";
    hint.textContent = "The linked SKU didn't specify this option — please make a selection.";
    parent.prepend(hint);
  },

  /** Remove a previously-added "needs selection" prompt, if present. */
  _clearNeedsSelection(parent) {
    if (!parent || !parent.classList.contains("sku-prefill-needs-selection")) return;
    parent.classList.remove("sku-prefill-needs-selection");
    parent.style.outline = "";
    parent.style.outlineOffset = "";
    parent.querySelector(".sku-prefill-hint")?.remove();
  },

  applyRestAfterFetch() {
    if (State.urlPrefillApplied) return;

    // ✅ FIX: If background fetch is still running, defer until it completes
    if (SkuCandidates.isBackgroundRunning()) {
      const poll = setInterval(() => {
        if (!SkuCandidates.isBackgroundRunning()) {
          clearInterval(poll);
          this._doApplyRest();
        }
      }, 200);
      return;
    }

    this._doApplyRest();
  },

  async _doApplyRest() {
    UrlPrefill._inProgress = true;
    console.log("[_doApplyRest] starting, params:", [...this._params.entries()]);

    // Was there any actual prefill intent in the URL at all (a "sku" param,
    // or direct per-handle params like "?rosset=r10&finish=ss")? _doApplyRest
    // also runs for plain manual browsing with NO query string — it stays
    // "armed" after the user picks the first section by hand — and in that
    // case an unmatched variant simply means "the user hasn't chosen this
    // yet," not "the SKU deliberately omitted it." Only in the latter case
    // should we auto-select/hide a blank option or flag a required one.
    const hadPrefillIntent = this._params.size > 0;

    // _selectVariant fires a synchronous `change` event which triggers async
    // updateSelections. We must await each one before moving to the next so
    // that section N's fetch completes before section N+1 tries to filter.
    // We also need to keep _inProgress = true throughout so gallery guards work.
    for (let i = 1; i < this._variants.length; i++) {
      const variant = this._variants[i];
      console.log(`[_doApplyRest] i=${i} variant="${variant}" hasParam=${this._params.has(variant)}`);
      if (!this._params.has(variant)) {
        if (!hadPrefillIntent) {
          // No URL prefill was ever in play — this is just a normal manual
          // accordion flow. Leave the section exactly as-is (unselected,
          // visible, no hint) like before any of this logic existed.
          continue;
        }
        // SKU omitted this segment entirely (e.g. no function code in
        // "NK-R05-SN"). That's not "unknown" — it's telling us the intended
        // value IS blank. If this section actually has a blank/no-value
        // option (a real "Blank" function tile with value=""), select it
        // explicitly so it flows through the same checked-input path as
        // every other section, instead of being left unselected — which
        // previously caused updateSelections() to treat Function as "not
        // yet chosen" and reset/lock everything after it (including Finish)
        // on every subsequent change event.
        const parent = document.querySelector(`[data-variant-handle="${variant}"]`);
        const hasBlankOption = parent && [...parent.querySelectorAll("input")].some(
          (inp) => (inp.value || inp.getAttribute("data-value") || "") === ""
        );
        console.log(`[_doApplyRest] variant="${variant}" hasBlankOption=${hasBlankOption}`);
        if (hasBlankOption) {
          // A merchant-provided "Blank"/"None" tile means this section is
          // genuinely optional for this product — auto-select it so the combo
          // completes, then tuck the section out of the way since there's
          // nothing meaningful left for the user to choose here.
          //
          // Register it in _params FIRST. filterOptions() runs a validity
          // check against State.skuCandidates whenever an earlier section's
          // change reveals/re-filters this one, and candidates typically have
          // no real function_code match for "blank" — without being flagged
          // as the URL-targeted value here, that check force-unchecks the
          // input we're about to select (see the `urlTargetVal` guard in
          // filterOptions), silently reverting our selection.
          this._params.set(variant, "");
          await this._selectVariantAsync(variant, "");
          if (parent) parent.style.display = "none";
        } else if (parent) {
          // No blank tile exists, so this section is a real choice the SKU
          // string simply didn't specify (e.g. no function code in
          // "NK-R05-SN"). Leave it visible and interactive — nothing was
          // auto-checked, no error is thrown — but flag it so it doesn't just
          // sit there looking unfinished; nudge the user to pick one.
          //this._flagNeedsSelection(parent);
        }
        continue;
      }
      await this._selectVariantAsync(variant, this._params.get(variant));
    }

    UrlPrefill._inProgress = false;
    State.urlPrefillApplied = true;
    console.log("[_doApplyRest] done, State.selectedValues:", { ...State.selectedValues });

    const allChosen = DOM.customVariants.length === Object.keys(State.selectedValues).length;
    if (allChosen) {
      Utils.showLoader();
      State.activeSkuHandle = `${State.productCode}${Object.values(State.selectedValues).join("")}`;
      resolveSkuProduct();
    }
  },

  /**
   * PowerCoat colours (e.g. "BKA") are never one of the plain visible finish
   * swatches — they live inside .pcc_wrapper, behind the generic ".pcc_variant"
   * "Powder Coat" tile which carries no code of its own. A normal click first
   * reveals that wrapper (see PccFlow.bind()) before the user can pick a colour.
   * When URL-prefill checks a with_pcc swatch directly (skipping the click),
   * we have to replay that same reveal or the correct swatch ends up checked
   * but invisible inside a still-collapsed/hidden wrapper.
   */
  _revealPccForInput(input) {
    if (!input || !input.classList.contains("with_pcc")) return;
    if (DOM.pccVariant) {
      DOM.pccVariant.style.display = "block";
      DOM.pccVariant.classList.add("selected");
    }
    if (DOM.pccWrapper) {
      DOM.pccWrapper.classList.remove("d-none");
    }
  },

  // Async wrapper: checks the input, sets checked, dispatches the event, and
  // waits for updateSelections to finish before returning.
  async _selectVariantAsync(variantName, paramValue) {
    const parent = document.querySelector(`[data-variant-handle="${variantName}"]`);
    if (!parent) { console.log(`[_selectVariantAsync] no parent for "${variantName}"`); return; }

    const normalizedParam = String(paramValue).toLowerCase().replace(/^-+/, "");
    const inputs = parent.querySelectorAll("input");
    let input = null;
    for (const inp of inputs) {
      const val = String(inp.value || inp.getAttribute("data-value") || "").toLowerCase().replace(/^-+/, "");
      if (val === normalizedParam) { input = inp; break; }
    }
    if (!input) {
      // No option in this section matches the URL/SKU value — skip it silently.
      // The _doApplyRest loop continues to the next variant.
      console.warn(`[UrlPrefill] No input matched "${paramValue}" for "${variantName}" — skipping, continuing with remaining variants.`);
      return;
    }
    console.log(`[_selectVariantAsync] checking "${variantName}" = "${paramValue}" (found input value="${input.value}")`);

    this._revealPccForInput(input);
    input.checked = true;
    console.log(`[_selectVariantAsync] input.checked now = ${input.checked}, dispatching updateSelections...`);
    // updateSelections is bound as a regular (non-async) handler from the
    // caller's perspective, but internally it's async. We call it directly
    // with the correct `this` context and await its promise.
    await updateSelections.call(input);
    console.log(`[_selectVariantAsync] updateSelections done for "${variantName}", input.checked = ${input.checked}`);
  },

  /**
   * Read all accordion variant URL params that have a matching visible input
   * and write them directly into State.selectedValues.
   * This lets updateFromCurrentSelection() use the full intended state
   * (including finish) immediately after the first-option fetch, without
   * waiting for the staggered applyRestAfterFetch() timeouts.
   */
  seedSelectedValuesFromUrl() {
    this._variants.forEach((variant) => {
      if (!variant || !this._params.has(variant)) return;
      const paramValue = this._params.get(variant);
      if (!paramValue) return;

      const parent = document.querySelector(`[data-variant-handle="${variant}"]`);
      if (!parent) return;

      const normalizedParam = String(paramValue).toLowerCase().replace(/^-+/, "");

      // Find matching input by normalized value (visible or not) inside this accordion section.
      const inputs = parent.querySelectorAll("input");
      let input = null;
      for (const inp of inputs) {
        const val = String(inp.value || inp.getAttribute("data-value") || "").toLowerCase().replace(/^-+/, "");
        if (val === normalizedParam) {
          input = inp;
          break;
        }
      }
      if (!input || !input.name) return;

      // PowerCoat colour matched inside .pcc_wrapper — keep the tile/wrapper
      // visually in sync with the state we're seeding.
      this._revealPccForInput(input);

      // Use input.name so the key matches candidate object properties (e.g. rosset_code,
      // function_code, finish) instead of the URL handle (backplate, function, finish).
      State.selectedValues[input.name] = input.value;
      console.log(`[UrlPrefill.seed] ${variant} -> ${input.name} = ${input.value}`);
    });
  },

  _selectVariant(variantName, paramValue) {
    const parent = document.querySelector(`[data-variant-handle="${variantName}"]`);
    if (!parent) return;

    const normalizedParam = String(paramValue).toLowerCase().replace(/^-+/, "");
    const inputs = parent.querySelectorAll("input");
    let input = null;
    for (const inp of inputs) {
      const val = String(inp.value || inp.getAttribute("data-value") || "").toLowerCase().replace(/^-+/, "");
      if (val === normalizedParam) {
        input = inp;
        break;
      }
    }

    if (!input) {
      // No option matches — leave this section unselected.
      // urlPrefillApplied stays false, so applyRestAfterFetch fires after
      // the user manually picks this section and the fetch completes.
      console.warn(`[UrlPrefill] No input matched "${paramValue}" for "${variantName}" — leaving unselected.`);
      return;
    }
    this._revealPccForInput(input);
    input.checked = true;
    input.dispatchEvent(new Event("change"));
  },
};


/* ============================================================================
 * 20. SPECBOOK DOWNLOAD (PDF generation via Cloudflare Worker)
 * ----------------------------------------------------------------------------
 * Sends the currently-resolved SKU product + metafields to a Zapier-backed
 * Cloudflare Worker that renders a spec sheet PDF and streams it back.
 * ========================================================================= */

const Specbook = {
  bindDownload() {
    const btn = DOM.specbookDownloadButton;
    if (!btn) return;

    btn.addEventListener("click", async (event) => {
      event.preventDefault();
      if (!State.activeSkuProduct) return;

      const trigger = event.currentTarget;
      trigger.disabled = true;
      const loader = trigger.querySelector(".loader");
      if (loader) loader.style.display = "inline-block";

      try {
        const payload = await this._buildPayload(State.activeSkuProduct, trigger);
        const zapierRes = await fetch(Config.SPECBOOK_WORKER_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cartData: { items: [payload] } }),
        });
        const zapierData = await zapierRes.json();
        if (zapierData.status !== 200 || !zapierData.pdf_url) {
          throw new Error("PDF not returned");
        }

        const pdfBlob = await fetch(zapierData.pdf_url).then((r) => r.blob());
        this._triggerDownload(pdfBlob);
      } catch (err) {
        console.error("Specbook download failed:", err);
        alert("Failed to download spec sheet. Please try again.");
      } finally {
        if (loader) loader.style.display = "none";
        trigger.disabled = false;
      }
    });
  },

  async _buildPayload(product, trigger) {
    const getMeta = (key) => product.metafields?.find((m) => m?.key === key)?.value || "";

    const productMedia = getMeta("product_media");
    const rawImage = productMedia
      ? productMedia.split(",")[0].trim()
      : product?.images?.edges?.[0]?.node?.url || trigger.dataset.image;

    const rawDimensionFile = getMeta("line_drawing") || null;

    // Verify each image URL is reachable before including it in the payload.
    // If an image is missing (404, network error, CORS block, etc.) it is
    // set to null so the PDF worker skips it instead of showing a broken image.
    const [productDimensionFile, productImage] = await Promise.all([
      this._checkImageUrl(rawDimensionFile),
      this._checkImageUrl(rawImage),
    ]);

    return {
      productId: trigger.dataset.epi || null,
      productTitle: product.title || null,
      productHandle: product.handle || null,
      productDescription: product.description || null,
      productDimensionFile,
      productSpecification: getMeta("technical_specification"),
      variantSKU: product.sku || trigger.dataset.sku,
      productImage,
    };
  },

  /**
   * Checks whether an image URL is actually loadable by attempting to load it
   * as an <img>. Returns the original URL on success (onload) or on timeout
   * (fail-open — a slow load isn't evidence the image is broken), and null
   * only on a confirmed failure (onerror, e.g. a genuine 404).
   *
   * NOTE: This intentionally does NOT use fetch(..., { mode: "no-cors" }).
   * With no-cors, cross-origin responses always come back as opaque
   * (status hidden) REGARDLESS of whether the request actually succeeded or
   * 404'd, so it can't distinguish a missing CloudFront asset from a valid
   * one. Loading via Image() doesn't require CORS headers to succeed and
   * correctly fires onerror for real HTTP failures / network errors.
   *
   * The tradeoff of Image()-based detection is that onload only fires once
   * the full image has downloaded (not just headers), so large/slow images
   * can legitimately take a few seconds. We give it a generous window and,
   * if it's still pending when that window closes, we assume it's fine
   * rather than penalizing a valid-but-slow asset.
   *
   * @param {string|null} url - The image URL to verify.
   * @returns {Promise<string|null>}
   */
  _checkImageUrl(url) {
    if (!url) return Promise.resolve(null);
    return new Promise((resolve) => {
      const img = new Image();
      let settled = false;
      const finish = (result) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolve(result);
      };

      img.onload = () => finish(url);
      img.onerror = () => {
        console.warn(`[Specbook] Image unavailable, skipping: ${url}`);
        finish(null);
      };

      // Generous window to allow full download of large/slow assets. If it's
      // still pending after this, fail OPEN (keep the URL) rather than drop
      // a valid image just because it hasn't finished loading yet — only a
      // confirmed onerror should ever result in null.
      const timeoutId = setTimeout(() => {
        console.warn(`[Specbook] Image check still pending after timeout, keeping optimistically: ${url}`);
        finish(url);
      }, 10000);

      img.src = url;
    });
  },

  _triggerDownload(blob) {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = "Spec Sheet.pdf";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  },
};


/* ============================================================================
 * 21. WISHLIST / SPECBOOK (Swym integration)
 * ----------------------------------------------------------------------------
 * "Specbook" in UI copy = Swym wishlist under the hood. 
 * 
 * SKU-BASED VALIDATION:
 * This implementation is "Finish-Aware". For products where multiple finishes 
 * share the same Variant ID (e.g. configurator shells), we use the 'fc' 
 * (finish code) stored in Swym's custom properties to distinguish them.
 * 
 * KEY FLOWS:
 * 1. Add to List: Captures IDs + Finish Metadata (fc, fn) into Swym cprops.
 * 2. Modal Rendering: Only marks a list as "selected" if the exact selected 
 *    variant AND finish match what's in the list.
 * 3. Icon State: Toggles red heart only if the specific selected variant 
 *    is in any of the user's lists.
 * ========================================================================= */

const Wishlist = {
  bindModal() {
    if (!DOM.wishlistModal) return;
    DOM.wishlistModal.addEventListener("show.bs.modal", (event) => {
      const trigger = event.relatedTarget;
      const createBtn = DOM.wishlistModal.querySelector(".create-wishlist-btn");

      if (window.isBulkOrderSpecbookFlow) {
        // Bulk order specbook flow has no single trigger element
        ["empi", "epi", "du", "wlh-image", "wlh-name", "wlh-fc", "wlh-fn"].forEach((k) => createBtn.removeAttribute(`data-${k}`));
        this.renderAllLists();
        return;
      }

      if (!trigger) return;

      // Scope search to THIS product's card so we don't accidentally
      // read another product's finish radio inputs from the same page.
      const productCard = trigger.closest('.card_product_block') || document;
      const activeFinish = productCard.querySelector('input[name="finish"]:checked')
        || productCard.querySelector('.swatch-input__input:checked')
        || productCard.querySelector('.swatch-input__input');

      const liveFc = trigger.getAttribute("data-wlh-fc")
        || (activeFinish ? activeFinish.value : null)
        || "";
      const liveFn = trigger.getAttribute("data-wlh-fn")
        || (activeFinish ? (activeFinish.getAttribute("title") || activeFinish.getAttribute("data-variant-title")) : null)
        || "";
      const liveImage = trigger.getAttribute("data-wlh-image")
        || (activeFinish ? activeFinish.getAttribute("data-variant-image") : null)
        || "";

      // Build the display title — strip any product-name prefix already baked into liveFn
      // so we never get "Catalina - Catalina - Satin Black Chrome" double-names.
      const rawProductName = trigger.getAttribute("data-wlh-name") || "";
      const baseProductName = rawProductName.split(" - ")[0];
      // If liveFn already starts with the product name, use it directly as the title
      const liveTitle = liveFn
        ? (liveFn.startsWith(baseProductName) ? liveFn : `${baseProductName} - ${liveFn}`)
        : baseProductName;

      console.log("🟢 [Modal Open] card:", productCard === document ? "document" : productCard.className);
      console.log("🟢 [Modal Open] liveFc:", liveFc, "| liveFn:", liveFn, "| liveImage:", liveImage);

      ["empi", "epi", "du"].forEach((k) => createBtn.setAttribute(`data-${k}`, trigger.getAttribute(`data-${k}`)));
      createBtn.setAttribute("data-wlh-image", liveImage);
      createBtn.setAttribute("data-wlh-name", liveTitle);
      createBtn.setAttribute("data-wlh-fc", liveFc);
      createBtn.setAttribute("data-wlh-fn", liveFn);

      // Read dropdown_product from cart-page wrapper (if present) and pass it through
      const cartWishlistWrapper = trigger.closest(".cart-item-wishlist-wrapper");
      const cartDropdownProduct = cartWishlistWrapper
        ? (cartWishlistWrapper.getAttribute("data-dropdown-product") || "")
        : "";
      if (cartDropdownProduct) {
        createBtn.setAttribute("data-dropdown-product", cartDropdownProduct);
      } else {
        createBtn.removeAttribute("data-dropdown-product");
      }

      // Read active add-on selection if present and stamp onto modal create button
      const activeAddonOpt = AddonDropdowns._findActiveSelection();
      if (activeAddonOpt) {
        const addonTitle =
          activeAddonOpt.getAttribute("data-dtitle") ||
          activeAddonOpt.getAttribute("data-dcode") ||
          activeAddonOpt.textContent.trim();
        const addonProductId = activeAddonOpt.value || activeAddonOpt.getAttribute("data-variant-id") || "";
        const addonVariantId = activeAddonOpt.getAttribute("data-variant-id") || activeAddonOpt.value || "";
        const addonImage = activeAddonOpt.getAttribute("data-dimage") || "";
        const addonUrl = activeAddonOpt.getAttribute("data-durl") || "";

        createBtn.setAttribute("data-addon-product-id", addonProductId);
        createBtn.setAttribute("data-addon-variant-id", addonVariantId);
        createBtn.setAttribute("data-addon-title", addonTitle);
        createBtn.setAttribute("data-addon-image", addonImage);
        createBtn.setAttribute("data-addon-url", addonUrl);
      } else {
        ["product-id", "variant-id", "title", "image", "url"].forEach((k) =>
          createBtn.removeAttribute(`data-addon-${k}`)
        );
      }

      this.renderAllLists(
        trigger.getAttribute("data-epi"),
        trigger.getAttribute("data-empi"),
        trigger.getAttribute("data-du"),
        liveImage,
        liveTitle,
        liveFc,
        liveFn
      );
    });
  },

  bindCreate() {
    const createBtn = document.querySelector(".create-wishlist-btn");
    if (!createBtn) return;

    createBtn.addEventListener("click", (event) => {
      const nameInput = document.querySelector('input[name="create_new_wishlist"]');
      const errorDiv = document.querySelector(".wishlist-error-div");

      if (nameInput.value === "") {
        errorDiv.innerHTML = "<p>Enter New Specbook Name</p>";
        return;
      }
      if (nameInput.value.length < 3) {
        errorDiv.innerHTML = "<p>Specbook name must be at least 3 characters long</p>";
        return;
      }

      _swat.fetchLists({
        callbackFn: (lists) => {
          const taken = lists.some((list) => {
            if (list.lname === nameInput.value) {
              alert("List name already exists");
              nameInput.value = "";
              nameInput.focus();
              return true;
            }
            return false;
          });
          if (!taken) {
            if (window.isBulkOrderSpecbookFlow) {
              this.createListBulk(nameInput.value);
            } else {
              const epi = event.target.getAttribute("data-epi");
              const empi = event.target.getAttribute("data-empi");
              const du = event.target.getAttribute("data-du");
              const iu = event.target.getAttribute("data-wlh-image");
              const dt = event.target.getAttribute("data-wlh-name");
              const fc = event.target.getAttribute("data-wlh-fc");
              const fn = event.target.getAttribute("data-wlh-fn");
              this.createList(nameInput.value, epi, empi, du, iu, dt, fc, fn);
            }
          }
        },
        errorFn: (error) => console.log("Error while Creating wishlist", error),
      });
    });
  },

  createList(lname, epi, empi, du, iu, dt, fc, fn) {
    _swat.createList(
      { lname },
      (newList) => {
        document.querySelector('input[name="create_new_wishlist"]').value = "";
        this.addToList(newList.lid, epi, empi, du, iu, dt, fc, fn);
      },
      (error) => console.log("Error while creating a List", error)
    );
  },

  createListBulk(lname) {
    _swat.createList(
      { lname },
      (newList) => {
        document.querySelector('input[name="create_new_wishlist"]').value = "";
        this.addBulkToList(newList.lid);
      },
      (error) => console.log("Error while creating a List", error)
    );
  },

  async addToList(lid, epi, empi, du, iu, dt, fc, fn, onErrorRollback) {
    // Generate a unique group_id for each SKU + its add-ons addition sequence (matching cart bundle logic)
    const groupId = 'g_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);

    const cleanEpi = String(epi).split("_grp")[0];
    const safeDu = du || window.location.href;
    const baseUrl = safeDu.split("?")[0].split("#")[0];
    const finalDu = `${baseUrl}?variant=${cleanEpi}&group_id=${groupId}`;

    const activeAddonOpt = AddonDropdowns._findActiveSelection();
    // If on the PDP, read from the live dropdown; if on the cart page or modal, fall back to stored attributes.
    const createBtn = document.querySelector(".create-wishlist-btn");
    const cartDropdownProduct = createBtn ? (createBtn.getAttribute("data-dropdown-product") || "") : "";

    const dropdownTitle = activeAddonOpt
      ? (activeAddonOpt.getAttribute("data-dtitle") || activeAddonOpt.getAttribute("data-dcode") || activeAddonOpt.textContent.trim())
      : (createBtn?.getAttribute("data-addon-title") || cartDropdownProduct || null);

    const dropdownProductId = activeAddonOpt
      ? (activeAddonOpt.value || activeAddonOpt.getAttribute("data-variant-id"))
      : (createBtn?.getAttribute("data-addon-product-id") || null);

    const dropdownVariantId = activeAddonOpt
      ? (activeAddonOpt.getAttribute("data-variant-id") || activeAddonOpt.value)
      : (createBtn?.getAttribute("data-addon-variant-id") || null);

    const dropdownImage = activeAddonOpt
      ? activeAddonOpt.getAttribute("data-dimage")
      : (createBtn?.getAttribute("data-addon-image") || null);

    const dropdownUrl = activeAddonOpt
      ? activeAddonOpt.getAttribute("data-durl")
      : (createBtn?.getAttribute("data-addon-url") || null);

    // Guard: only store cprops if fc is a real non-null, non-empty string
    const validFc = fc && fc !== "null" && fc !== "undefined" ? fc.replace(/^-/, "") : null;
    const validFn = fn && fn !== "null" && fn !== "undefined" ? fn : "";
    const cprops = {
      ...(validFc ? { fc: validFc, fn: validFn } : {}),
      ...(dropdownTitle ? { dropdown_product: dropdownTitle, dropdown_variant_id: dropdownVariantId } : {}),
      group_id: groupId,
      _group_id: groupId,
      real_epi: cleanEpi,
    };
    const hasCprops = Object.keys(cprops).length > 0;
    // NOTE: empi and epi must be clean Shopify IDs so Swym validates them server-side.
    // cprops.group_id is what disambiguates same-empi entries on read.
    const product = { epi: cleanEpi, empi, du: finalDu, iu, dt, ...(hasCprops && { cprops }) };

    console.log("🟡 [addToList] fc:", fc, "| fn:", fn, "| cprops:", cprops);

    // Helper to add the main SKU product to Swym list (added LAST so it appears at top)
    const submitMainProduct = () => {
      console.log("[Specbook] Adding main product to list (last for top ordering):", lid, product);
      _swat.addToList(
        lid,
        product,
        () => {
          this.renderAllLists(epi, empi, du, iu, dt, fc, fn);
          this.refreshButtonState(epi, empi, du);
        },
        (error) => {
          console.error("[Specbook] Error while adding the Product to the List", error);
          if (typeof this.showToast === "function") {
            this.showToast(error?.msg === "Connection refused" ? "Unable to connect to Specbook service. Check your connection or ad blocker." : "Failed to add item to Specbook.");
          }
          if (typeof onErrorRollback === "function") {
            onErrorRollback(error);
          }
        }
      );
    };

    // If an add-on product from dropdown or stored attribute is selected,
    // resolve and add each add-on FIRST so that when the main product is added LAST,
    // the main product appears at the TOP (first position) in Wishlist/SpecBook.
    if (activeAddonOpt || dropdownVariantId) {
      if (activeAddonOpt) {
        try {
          const selectedFinishInput = document.querySelector('input[name="finish"]:checked');
          const finishCode = selectedFinishInput ? String(selectedFinishInput.value || "").trim() : "";

          const baseCode = activeAddonOpt.getAttribute("data-dcode");
          const bundleHandle = activeAddonOpt.getAttribute("data-dhandle");

          // Helper: resolve a SKU product by handle
          const fetchSkuProduct = async (h) => {
            try {
              const res = await gql(Queries.resolvedSkuProduct, { handle: h });
              return res?.product || null;
            } catch (e) { return null; }
          };

          // Helper: add one component entry to the SpecBook list
          const addComponentToList = (addonProduct) => new Promise((resolve) => {
            if (addonProduct.du) {
              const baseAddonUrl = addonProduct.du.split("#")[0];
              addonProduct.du = `${baseAddonUrl}#group_id=${groupId}`;
            }
            if (addonProduct.epi) {
              const cleanAddonEpi = String(addonProduct.epi).split("_grp")[0];
              if (!addonProduct.cprops) addonProduct.cprops = {};
              addonProduct.cprops.real_epi = cleanAddonEpi;
              addonProduct.epi = cleanAddonEpi;
            }
            const addonQty = addonProduct.qty || 1;
            console.log("🚀 [Specbook addComponentToList] Submitting addon product (first):", lid, addonProduct, "| group:", groupId, "| qty:", addonQty);
            this.upsertAddonForGroup(lid, addonProduct, groupId, addonQty, (res, error) => {
              if (error) {
                console.error("❌ [Specbook addComponentToList] Error adding addon product:", error);
              } else {
                console.log("✅ [Specbook addComponentToList] Addon product added/merged successfully:", addonProduct.dt, res);
              }
              resolve(); // Resolve either way so the loop/flow continues
            });
          });

          const addonCprops = { is_addon: true, group_id: groupId, _group_id: groupId };
          if (dt) addonCprops.main_product = dt;

          if (baseCode) {
            // ── Non-bundle SKU option: resolve single SKU by finish ──
            let componentQty = null;
            const optTitle = activeAddonOpt.getAttribute("data-dtitle") || activeAddonOpt.textContent || "";
            const qtyMatch = optTitle.match(/(\d+)\s*x/i);
            if (qtyMatch) componentQty = parseInt(qtyMatch[1], 10);

            let skuHandle = finishCode ? `${baseCode}${finishCode}` : "";
            let skuProduct = skuHandle ? await fetchSkuProduct(skuHandle) : null;
            let resolvedFinishName = selectedFinishInput?.getAttribute("title") || "";
            let resolvedFinishCode = finishCode ? finishCode.replace(/^-/, "") : "";
            // Fallback sequence: PSS→["-PC","-SS"], SSS→["-SN","-SS"], else→["-SS"]
            if (!skuProduct) {
              for (const fb of sparePartFallbackFinishes(finishCode)) {
                skuProduct = await fetchSkuProduct(`${baseCode}${fb}`);
                if (skuProduct) {
                  resolvedFinishName = fb === "-PC" ? "Polished Chrome" : fb === "-SN" ? "Satin Nickel" : "Satin Stainless Steel";
                  resolvedFinishCode = fb.replace(/^-/, "");
                  break;
                }
              }
            }

            if (skuProduct) {
              if (resolvedFinishCode) addonCprops.fc = resolvedFinishCode;
              if (resolvedFinishName) addonCprops.fn = resolvedFinishName;
            } else {
              if (validFc) addonCprops.fc = validFc;
              if (validFn) addonCprops.fn = validFn;
            }

            let addonEpi = dropdownVariantId;
            let addonEmpi = dropdownProductId || dropdownVariantId;
            let addonTitle = dropdownTitle;
            let addonImage = dropdownImage || iu || "";
            let addonUrl = dropdownUrl
              ? `${dropdownUrl.split("?")[0].split("#")[0]}?variant=${dropdownVariantId}&group_id=${groupId}`
              : `${baseUrl}?variant=${dropdownVariantId}&group_id=${groupId}`;

            if (skuProduct) {
              const skuVariant = skuProduct.variants?.edges?.[0]?.node;
              if (skuVariant) {
                addonEpi = Utils.gidToId(skuVariant.id);
                addonEmpi = Utils.gidToId(skuProduct.id);
                const rawTitle = skuProduct.title || baseCode;
                addonTitle = componentQty && componentQty > 1 ? `${componentQty} x ${rawTitle}` : rawTitle;
                if (skuVariant.image?.url || skuProduct.images?.edges?.[0]?.node?.url) {
                  addonImage = skuVariant.image?.url || skuProduct.images?.edges[0].node.url;
                }
                addonUrl = `${document.location.origin}${window.Shopify?.routes?.root || "/"}products/${skuProduct.handle}?variant=${addonEpi}&group_id=${groupId}`;
              }
            }

            const addonProduct = {
              epi: addonEpi,
              empi: addonEmpi,
              du: addonUrl,
              iu: addonImage,
              dt: addonTitle,
              ...(componentQty ? { qty: componentQty } : {}),
              ...(Object.keys(addonCprops).length > 0 && { cprops: addonCprops }),
            };
            await addComponentToList(addonProduct);

          } else if (bundleHandle) {
            // ── Native Bundle option: resolve ALL components separately ──
            let components = [];
            try {
              const data = await gql(Queries.bundleComponentProduct, { handle: bundleHandle });
              components = data?.product?.selectedOrFirstAvailableVariant?.components?.nodes || [];
            } catch (err) {
              console.error("[Specbook] bundle component fetch failed:", err);
            }

            if (components.length > 0) {
              for (const component of components) {
                const componentTitle = component?.productVariant?.product?.title;
                const componentQty = Number(component?.quantity) > 0 ? Number(component.quantity) : 1;
                const componentVariantIdRaw = component?.productVariant?.id;
                const componentVariantId = componentVariantIdRaw ? Utils.gidToId(componentVariantIdRaw) : null;
                const componentHandle = component?.productVariant?.product?.handle || null;

                if (!componentTitle && !componentVariantId) continue;

                let addonEpi = componentVariantId;
                let addonEmpi = componentVariantId;
                let addonTitle = componentQty > 1 ? `${componentQty} x ${componentTitle || ""}` : (componentTitle || "");
                let addonImage = dropdownImage || iu || "";
                let addonUrl = componentHandle
                  ? `${document.location.origin}${window.Shopify?.routes?.root || "/"}products/${componentHandle}?variant=${componentVariantId}&group_id=${groupId}`
                  : `${baseUrl}?variant=${componentVariantId}&group_id=${groupId}`;

                if (componentTitle) {
                  let skuProduct = null;
                  if (finishCode) skuProduct = await fetchSkuProduct(`${componentTitle}${finishCode}`);
                  if (!skuProduct) {
                    for (const fb of sparePartFallbackFinishes(finishCode)) {
                      skuProduct = await fetchSkuProduct(`${componentTitle}${fb}`);
                      if (skuProduct) break;
                    }
                  }

                  if (skuProduct) {
                    const skuVariant = skuProduct.variants?.edges?.[0]?.node;
                    if (skuVariant) {
                      addonEpi = Utils.gidToId(skuVariant.id);
                      addonEmpi = Utils.gidToId(skuProduct.id);
                      const rawTitle = skuProduct.title || componentTitle;
                      addonTitle = componentQty > 1 ? `${componentQty} x ${rawTitle}` : rawTitle;
                      if (skuVariant.image?.url || skuProduct.images?.edges?.[0]?.node?.url) {
                        addonImage = skuVariant.image?.url || skuProduct.images?.edges[0].node.url;
                      }
                      addonUrl = `${document.location.origin}${window.Shopify?.routes?.root || "/"}products/${skuProduct.handle}?variant=${addonEpi}&group_id=${groupId}`;
                    }
                  }
                }

                const addonProduct = {
                  epi: addonEpi,
                  empi: addonEmpi,
                  du: addonUrl,
                  iu: addonImage,
                  dt: addonTitle,
                  qty: componentQty,
                  ...(Object.keys(addonCprops).length > 0 && { cprops: addonCprops }),
                };
                await addComponentToList(addonProduct);
              }
            } else {
              const addonEpi = dropdownVariantId;
              const addonEmpi = dropdownProductId || dropdownVariantId;
              const addonTitle = dropdownTitle;
              const addonImage = dropdownImage || iu || "";
              const addonUrl = dropdownUrl
                ? `${dropdownUrl.split("?")[0].split("#")[0]}?variant=${dropdownVariantId}&group_id=${groupId}`
                : `${baseUrl}?variant=${dropdownVariantId}&group_id=${groupId}`;
              const addonProduct = {
                epi: addonEpi,
                empi: addonEmpi,
                du: addonUrl,
                iu: addonImage,
                dt: addonTitle,
                ...(Object.keys(addonCprops).length > 0 && { cprops: addonCprops }),
              };
              await addComponentToList(addonProduct);
            }
          } else {
            const addonEpi = dropdownVariantId;
            const addonEmpi = dropdownProductId || dropdownVariantId;
            const addonTitle = dropdownTitle;
            const addonImage = dropdownImage || iu || "";
            const addonUrl = dropdownUrl
              ? `${dropdownUrl.split("?")[0].split("#")[0]}?variant=${dropdownVariantId}&group_id=${groupId}`
              : `${baseUrl}?variant=${dropdownVariantId}&group_id=${groupId}`;
            const addonProduct = {
              epi: addonEpi,
              empi: addonEmpi,
              du: addonUrl,
              iu: addonImage,
              dt: addonTitle,
              ...(Object.keys(addonCprops).length > 0 && { cprops: addonCprops }),
            };
            await addComponentToList(addonProduct);
          }

        } catch (err) {
          console.error("[Specbook] Error resolving addon SKU products:", err);
        }

        // Add main SKU product LAST after addons are finished
        submitMainProduct();

      } else {
        // Fallback: no live dropdown, use stored attributes (cart-page flow)
        const realAddonEpi = dropdownVariantId;
        const realAddonEmpi = dropdownProductId || dropdownVariantId;
        const addonTitle = dropdownTitle;
        const addonImage = dropdownImage || iu || "";
        let addonUrl = dropdownUrl
          ? `${dropdownUrl.split("?")[0].split("#")[0]}?variant=${dropdownVariantId}`
          : `${baseUrl}?variant=${dropdownVariantId}`;
        if (addonUrl) {
          const baseAddonUrl = addonUrl.split("#")[0];
          addonUrl = baseAddonUrl.includes("?")
            ? `${baseAddonUrl}&group_id=${groupId}`
            : `${baseAddonUrl}?group_id=${groupId}`;
        }
        const addonCprops = {
          is_addon: true,
          group_id: groupId,
          _group_id: groupId,
          real_epi: realAddonEpi,
          real_empi: realAddonEmpi,
        };
        if (dt) addonCprops.main_product = dt;
        if (validFc) addonCprops.fc = validFc;
        if (validFn) addonCprops.fn = validFn;
        const cleanAddonEpi = realAddonEpi ? String(realAddonEpi).split("_grp")[0] : realAddonEpi;
        const addonProduct = {
          epi: cleanAddonEpi,
          empi: realAddonEmpi,
          du: addonUrl,
          iu: addonImage,
          dt: addonTitle,
          ...(Object.keys(addonCprops).length > 0 && { cprops: addonCprops }),
        };
        console.log("[Specbook] Adding fallback addon product to list (first):", lid, addonProduct);
        this.upsertAddonForGroup(lid, addonProduct, groupId, 1, (res, error) => {
          if (error) console.error("[Specbook] Error adding addon product to list:", error);
          submitMainProduct();
        });
      }
    } else {
      submitMainProduct();
    }
  },

  // ==========================================================================
  // GROUP-AWARE ADD-ON BOOKKEEPING
  // --------------------------------------------------------------------------
  // Swym enforces "one unique list item per (empi, epi) per list" — see
  // https://developers.getswym.com/docs/add-to-list ("epi: ...  // one unique
  // list item (empi+epi) per listid"). There is no way to get two real Swym
  // line items for the same add-on variant in the same list, so suffixing epi
  // (like we already tried and reverted for empi) can't work either — Swym
  // will just collapse repeat adds of the same (empi, epi) into one item,
  // which is exactly the "only 4 items instead of 6" symptom.
  //
  // Fix: keep ONE Swym item per add-on (empi, epi), and track which SKU
  // groups need it — and how much of it — ourselves, in
  // cprops.groups = { [groupId]: { qty, main_product } }. The item's own
  // `qty` is kept as the sum across all groups (so Swym's own admin/qty
  // reporting stays sane); the SpecBook page renders one row per group entry
  // using that per-group qty, and removing one SKU only strips its groupId
  // out of the map instead of deleting the shared item outright.
  // ==========================================================================

  findExistingListItem(lid, empi, epi, onFound) {
    const targetEpi = String(epi).split("_grp")[0];
    const fetchFn = (swat) => {
      swat.fetchListDetails(
        { lid },
        (listDetails) => {
          const match = (listDetails?.items || []).find((it) => {
            const itEpi = it.cprops?.real_epi || String(it.epi).split("_grp")[0];
            return String(it.empi) === String(empi) && String(itEpi) === String(targetEpi);
          });
          onFound(match || null);
        },
        () => onFound(null)
      );
    };
    if (window._swat) fetchFn(window._swat);
    else {
      if (!window.SwymCallbacks) window.SwymCallbacks = [];
      window.SwymCallbacks.push((swat) => fetchFn(swat));
    }
  },

  // Add (or increment) this add-on for one SKU group, without clobbering any
  // other group that already needs the same add-on.
  upsertAddonForGroup(lid, addonProduct, groupId, qty, onDone) {
    this.findExistingListItem(lid, addonProduct.empi, addonProduct.epi, (existing) => {
      const groups = { ...(existing?.cprops?.groups || {}) };
      groups[groupId] = {
        qty,
        main_product: addonProduct.cprops?.main_product || existing?.cprops?.main_product || "",
      };
      const totalQty = Object.values(groups).reduce((sum, g) => sum + (Number(g.qty) || 0), 0);
      const mergedCprops = {
        ...(existing?.cprops || {}),
        ...(addonProduct.cprops || {}),
        groups,
        // group_id/_group_id are legacy scalars from before multi-group
        // support; keep them pointed at this newest group for backward
        // compatibility with any code still reading them directly.
        group_id: groupId,
        _group_id: groupId,
      };
      const merged = { ...addonProduct, qty: totalQty, cprops: mergedCprops };
      _swat.addToList(lid, merged, () => onDone && onDone(merged), (err) => onDone && onDone(null, err));
    });
  },

  // Remove one SKU group's need for this add-on. Only actually deletes the
  // Swym item once no group references it anymore; otherwise updates it with
  // the trimmed group map and reduced qty.
  removeAddonFromGroup(lid, item, groupId, onDone) {
    const groups = { ...(item.cprops?.groups || {}) };
    const hadGroupsMap = Object.keys(groups).length > 0;
    delete groups[groupId];

    const remainingGroupIds = Object.keys(groups);
    if (hadGroupsMap && remainingGroupIds.length > 0) {
      const totalQty = Object.values(groups).reduce((sum, g) => sum + (Number(g.qty) || 0), 0);
      const updated = {
        epi: item.epi,
        empi: item.empi,
        du: item.du,
        iu: item.iu,
        dt: item.dt,
        qty: totalQty,
        cprops: { ...(item.cprops || {}), groups },
      };
      _swat.addToList(lid, updated, () => onDone && onDone(), (err) => onDone && onDone(err));
    } else {
      // No other group needs this add-on (or it predates the groups map) —
      // safe to delete outright, matching the old behavior.
      _swat.deleteFromList(
        lid,
        { epi: item.epi, empi: item.empi, du: item.du },
        () => onDone && onDone(),
        (err) => onDone && onDone(err)
      );
    }
  },

  // Update one SKU group's need-quantity for this add-on, without touching
  // any other group that shares it. Mirrors upsertAddonForGroup/
  // removeAddonFromGroup: recomputes the item's total qty as the sum across
  // all groups and round-trips cprops.groups exactly, instead of clobbering
  // the shared item with a flat qty update.
  updateAddonGroupQty(lid, item, groupId, newQty, onDone) {
    const groups = { ...(item.cprops?.groups || {}) };
    if (Object.keys(groups).length > 0 && Object.prototype.hasOwnProperty.call(groups, groupId)) {
      groups[groupId] = { ...groups[groupId], qty: newQty };
      const totalQty = Object.values(groups).reduce((sum, g) => sum + (Number(g.qty) || 0), 0);
      const updated = {
        epi: item.epi,
        empi: item.empi,
        du: item.du,
        iu: item.iu,
        dt: item.dt,
        qty: totalQty,
        cprops: { ...(item.cprops || {}), groups },
      };
      _swat.addToList(lid, updated, () => onDone && onDone(updated), (err) => onDone && onDone(null, err));
    } else {
      // No groups map (legacy single-SKU add-on) — safe to update qty directly.
      _swat.updateListItem(lid, { epi: item.epi, empi: item.empi, du: item.du, qty: newQty }, (updated) => onDone && onDone(updated), (err) => onDone && onDone(null, err));
    }
  },

  addBulkToList(lid) {
    const products = window.bulkResolvedProducts || [];
    const unresolvedSkus = window.bulkUnresolvedSkus || [];
    if (products.length === 0 && unresolvedSkus.length === 0) return;

    const loader = document.getElementById('bulk-specbook-loader');
    const successMsg = document.getElementById('success-message');
    const errorMessage = document.getElementById('error-message');

    if (loader) loader.style.display = 'inline-block';
    if (successMsg) successMsg.innerText = '';
    if (errorMessage) errorMessage.innerHTML = '';

    if (DOM.wishlistModal) {
      const modalInstance = bootstrap.Modal.getInstance(DOM.wishlistModal);
      if (modalInstance) modalInstance.hide();
    }

    let addedCount = 0;
    let pendingCount = products.length;
    const addErrors = [];

    const finishBulkAdd = () => {
      if (loader) loader.style.display = 'none';
      if (addedCount > 0) {
        successMsg.innerText = `✅ ${addedCount} item(s) added to Spec Book!`;
        const specbookActions = document.getElementById('cart-actions');
        if (specbookActions) {
          specbookActions.innerHTML = `
            <button type="button" id="view-specbook-btn" style="margin-top:15px !important; padding:10px 20px !important; background:#28a745 !important; color:#fff !important; border:none !important; cursor:pointer !important; font-size:14px !important; min-width:150px !important; height:40px !important; text-align:center !important;">View Spec Book</button>
          `;
          document.getElementById('view-specbook-btn').addEventListener('click', function () {
            window.location.href = window.Shopify?.routes?.root + 'pages/my-specbooks';
          });
        }

        // Clear the bulk order form fields
        const formContainer = document.getElementById('cart-add-form');
        if (formContainer) {
          const container = document.getElementById('items-container');
          if (container) {
            container.innerHTML = `
              <div class="item-row">
                <input type="text" class="sku-input" placeholder="Enter SKU" required>
                <select class="qty-input" required>
                  <option value="1" selected>1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                </select>
                <button type="button" class="remove-item" disabled style="visibility: hidden;">Remove</button>
              </div>
            `;
          }
          formContainer.querySelectorAll('textarea').forEach((t) => (t.value = ''));
          const fileInput = document.getElementById('csv-file-input');
          if (fileInput) fileInput.value = '';
        }
      }

      const allErrors = [...unresolvedSkus, ...addErrors];
      if (allErrors.length > 0 && errorMessage) {
        errorMessage.innerHTML = `<ul style="margin:8px 0;padding-left:20px;">${allErrors.map(s => `<li style="color:red;">${s}</li>`).join('')}</ul>`;
      }
      window.isBulkOrderSpecbookFlow = false;
      window.bulkResolvedProducts = null;
      window.bulkUnresolvedSkus = null;
    };

    if (products.length === 0) {
      finishBulkAdd();
      return;
    }

    products.forEach((product) => {
      const baseUrl = `${window.location.origin}${window.Shopify?.routes?.root || '/'}products/${product.handle}`;
      const finalDu = `${baseUrl}?variant=${product.epi}`;
      const productPayload = {
        epi: product.epi,
        empi: product.empi,
        du: finalDu,
        iu: product.image || '',
        dt: product.title || product.handle,
      };

      _swat.addToList(
        lid,
        productPayload,
        () => {
          addedCount++;
          pendingCount--;
          if (pendingCount === 0) {
            finishBulkAdd();
          }
        },
        (err) => {
          pendingCount--;
          addErrors.push(`"${product.handle}" - Spec Book error`);
          console.error('[SpecBook] addToList error:', err);
          if (pendingCount === 0) {
            finishBulkAdd();
          }
        }
      );
    });
  },

  renderAllLists(epi, empi, du, iu, dt, fc, fn) {
    const fetchFn = (swatInstance) => {
      swatInstance.fetchLists({
        callbackFn: (lists) => this._renderLists(lists, epi, empi, du, iu, dt, fc, fn),
        errorFn: (error) => console.log("Error while fetching all Lists", error),
      });
    };

    if (window._swat) {
      fetchFn(window._swat);
    } else {
      if (!window.SwymCallbacks) window.SwymCallbacks = [];
      window.SwymCallbacks.push((swat) => fetchFn(swat));
    }
  },

  _renderLists(lists, epi, empi, du, iu, dt, fc, fn) {
    const container = document.querySelector(".collection-lists");
    if (!container) return;
    container.innerHTML = "";

    lists.forEach((list) => {
      if (window.isBulkOrderSpecbookFlow) {
        const listDiv = document.createElement("div");
        listDiv.classList.add("collection-item", "d-flex", "align-items-center");
        listDiv.innerHTML = `
          <div class="wishlist-title-name col-9"><h4><strong>${list.lname}</strong></h4></div>
          <div class="wishlist-item-btn col-3">
            <button class="btn btn-outline-secondary select-collection-btn"
                    data-lid="${list.lid}">Select</button>
          </div>`;
        container.appendChild(listDiv);

        listDiv.querySelector(".select-collection-btn").addEventListener("click", () => {
          this.addBulkToList(list.lid);
        });
        return;
      }

      const inThisList = list.listcontents.some((c) => {
        const itemCleanEpi = c.cprops?.real_epi || String(c.epi).split("_grp")[0];
        const idMatch = String(c.empi) === String(empi) && String(itemCleanEpi) === String(epi);
        if (!idMatch) return false;

        // If IDs match, but we have finish metadata, ensure the finishes also match.
        if (c.cprops && c.cprops.fc && fc) {
          const cleanCfc = String(c.cprops.fc).replace(/^-/, "").toUpperCase();
          const cleanFc = String(fc).replace(/^-/, "").toUpperCase();
          return `-${cleanFc}-`.includes(`-${cleanCfc}-`) || `-${cleanCfc}-`.includes(`-${cleanFc}-`);
        }
        return true;
      });
      const listDiv = document.createElement("div");
      listDiv.classList.add("collection-item", "d-flex", "align-items-center");
      if (inThisList) listDiv.classList.add("selected-wishlist");

      const buttonText = inThisList ? "Remove From Wishlist" : "Add To Wishlist";
      const buttonAttr = `data-epi="${epi}" data-iu="${(iu || '').replace(/"/g, '&quot;')}" data-dt="${(dt || '').replace(/"/g, '&quot;')}" data-fc="${(fc || '').replace(/"/g, '&quot;')}" data-fn="${(fn || '').replace(/"/g, '&quot;')}"`;

      listDiv.innerHTML = `
        <div class="wishlist-title-name col-9"><h4><strong>${list.lname}</strong></h4></div>
        <div class="wishlist-item-btn col-3">
          <button class="btn btn-outline-secondary select-collection-btn"
                  data-lid="${list.lid}"${buttonAttr}>${buttonText}</button>
        </div>
        <a href="#" data-remove-lid="${list.lid}"
           class="button button--tertiary col-3 d-none remove-productBtn">
          <img class="icon-remove"
               src="https://cdn.shopify.com/s/files/1/0881/1662/5712/files/delete_bin.png?v=1735982593"
               alt="Delete" width="auto" height="auto">
        </a>`;
      container.appendChild(listDiv);

      // Delete-entire-list button.
      listDiv.querySelector(".remove-productBtn").addEventListener("click", (event) => {
        event.preventDefault();
        if (!confirm("Are you sure you want to delete this collection?")) return;
        _swat.deleteList(
          list.lid,
          () => {
            this.renderAllLists(epi, empi, du, iu, dt, fc, fn);
            this.refreshButtonState(epi, empi, du);
          },
          (error) => console.log("Error while deleting the collection", error)
        );
      });

      // Add / Remove toggle button.
      listDiv.querySelector(".select-collection-btn").addEventListener("click", () => {
        const btn = listDiv.querySelector(".select-collection-btn");
        const isSelected = listDiv.classList.contains("selected-wishlist") || btn.innerText === "Remove From Wishlist";

        if (isSelected) {
          // Optimistically update UI immediately (removes tick mark instantly)
          btn.innerText = "Add To Wishlist";
          listDiv.classList.remove("selected-wishlist");

          const matchingItems = list.listcontents.filter((c) => {
            const itemCleanEpi = c.cprops?.real_epi || String(c.epi).split("_grp")[0];
            const idMatch = String(c.empi) === String(empi) && String(itemCleanEpi) === String(epi);
            if (!idMatch) return false;
            if (c.cprops && c.cprops.fc && fc) {
              const cleanCfc = String(c.cprops.fc).replace(/^-/, "").toUpperCase();
              const cleanFc = String(fc).replace(/^-/, "").toUpperCase();
              return `-${cleanFc}-`.includes(`-${cleanCfc}-`) || `-${cleanCfc}-`.includes(`-${cleanFc}-`);
            }
            return true;
          });

          // Main SKU items are never shared across groups, so they can be
          // blind-deleted. Add-ons CAN be shared (the same add-on, e.g.
          // SC143-SS, attached to two different main SKUs) — since Swym only
          // allows one item per (empi, epi) per list, that shared add-on is a
          // SINGLE Swym item whose cprops.groups tracks every SKU group that
          // needs it. Removing one SKU must only strip that SKU's groupId out
          // of the map (removeAddonFromGroup), not delete the whole item out
          // from under the other SKU that still needs it.
          const itemsToDelete = [];
          const addonsToUngroup = []; // { item, groupId }
          matchingItems.forEach((mainItem) => {
            itemsToDelete.push({ epi: mainItem.epi, empi: mainItem.empi, du: mainItem.du });
            let parentGroupId = mainItem.cprops?.group_id || mainItem.cprops?._group_id || '';
            if (!parentGroupId && mainItem.du) {
              try {
                if (mainItem.du.includes('#group_id=')) {
                  parentGroupId = mainItem.du.split('#group_id=')[1].split('&')[0];
                } else {
                  const duUrl = new URL(mainItem.du, window.location.origin);
                  parentGroupId = duUrl.searchParams.get('group_id') || '';
                }
              } catch (e) { }
            }
            const mainTitle = (mainItem.dt || dt || '').trim().toLowerCase();

            list.listcontents.forEach((c) => {
              if (c.epi === mainItem.epi && c.du === mainItem.du) return;
              const itemIsAddon = c.cprops?.is_addon || false;
              if (!itemIsAddon) return;

              // Prefer the per-group map when present: an add-on belongs to
              // this SKU's removal if THIS parentGroupId is one of its keys,
              // regardless of which group cprops.group_id happens to point at
              // (that scalar only ever reflects the most-recently-added group).
              const itemGroups = c.cprops?.groups || null;
              let isMatch = false;
              if (itemGroups && Object.keys(itemGroups).length > 0) {
                isMatch = parentGroupId ? Object.prototype.hasOwnProperty.call(itemGroups, parentGroupId) : false;
              } else {
                let itemGroupId = c.cprops?.group_id || c.cprops?._group_id || '';
                if (!itemGroupId && c.du) {
                  try {
                    if (c.du.includes('#group_id=')) {
                      itemGroupId = c.du.split('#group_id=')[1].split('&')[0];
                    } else {
                      const itemUrl = new URL(c.du, window.location.origin);
                      itemGroupId = itemUrl.searchParams.get('group_id') || '';
                    }
                  } catch (e) { }
                }
                if (parentGroupId && itemGroupId) {
                  isMatch = (parentGroupId === itemGroupId);
                } else if (!parentGroupId && !itemGroupId) {
                  const itemMainProduct = (c.cprops?.main_product || c.cprops?.['Main Product'] || '').trim().toLowerCase();
                  if (mainTitle && itemMainProduct && (itemMainProduct === mainTitle || mainTitle.includes(itemMainProduct) || itemMainProduct.includes(mainTitle))) {
                    isMatch = true;
                  }
                }
              }
              if (isMatch) {
                addonsToUngroup.push({ item: c, groupId: parentGroupId });
              }
            });
          });

          if (itemsToDelete.length === 0 && addonsToUngroup.length === 0) {
            itemsToDelete.push({ epi, empi, du });
          }

          const deletePromises = itemsToDelete.map((itemToDelete) => {
            return new Promise((resolve) => {
              _swat.deleteFromList(
                list.lid,
                itemToDelete,
                () => resolve(),
                (error) => {
                  console.log("Error while deleting product item", error);
                  resolve();
                }
              );
            });
          });

          const ungroupPromises = addonsToUngroup.map(({ item, groupId }) => {
            return new Promise((resolve) => {
              this.removeAddonFromGroup(list.lid, item, groupId, (error) => {
                if (error) console.log("Error while removing add-on from group", error);
                resolve();
              });
            });
          });

          Promise.all([...deletePromises, ...ungroupPromises]).then(() => {
            this.refreshButtonState(epi, empi, du);
          });
        } else {
          // Optimistically update UI immediately (shows tick mark INSTANTLY with 0ms delay!)
          btn.innerText = "Remove From Wishlist";
          listDiv.classList.add("selected-wishlist");

          const iu_val = btn.getAttribute("data-iu");
          const dt_val = btn.getAttribute("data-dt");
          const fc_val = btn.getAttribute("data-fc");
          const fn_val = btn.getAttribute("data-fn");
          this.addToList(list.lid, epi, empi, du, iu_val, dt_val, fc_val, fn_val, (error) => {
            // Rollback UI state on error
            btn.innerText = "Add To Wishlist";
            listDiv.classList.remove("selected-wishlist");
          });
        }
      });
    });
  },

  /**
   * Reflect current wishlist state in all .js_specbook_button icons —
   * red heart if the SKU is in any list, else outlined heart.
   */
  refreshButtonState(epi, empi, du) {
    const fetchFn = (swatInstance) => {
      swatInstance.fetchLists({
        callbackFn: (lists) => this._paintButtons(lists),
        errorFn: (error) => console.log("Error while fetching all Lists", error),
      });
    };

    if (window._swat) {
      fetchFn(window._swat);
    } else {
      if (!window.SwymCallbacks) window.SwymCallbacks = [];
      window.SwymCallbacks.push((swat) => fetchFn(swat));
    }
  },

  _paintButtons(lists) {
    const WISHLISTED_ICON = "https://cdn.shopify.com/s/files/1/0881/1662/5712/files/heart-red.svg?v=1748924116&width=20";
    const DEFAULT_ICON = "https://cdn.shopify.com/s/files/1/0881/1662/5712/files/heart_1.svg?v=1748848591&width=20";

    document.querySelectorAll(".js_specbook_button").forEach((el) => {
      const empi = el.getAttribute("data-empi");
      const epi = el.getAttribute("data-epi");
      const fc = el.getAttribute("data-wlh-fc"); // Current selected finish code
      const img = el.querySelector("img");
      const isWishlisted = lists.some((list) =>
        list.listcontents.some((c) => {
          // 1. Primary match on IDs
          const idMatch = String(c.empi) === String(empi) && String(c.epi) === String(epi);
          if (!idMatch) return false;

          // 2. If IDs match, but we have finish metadata, ensure the finishes also match.
          // Pad with hyphens to support cases where the finish code is in the middle of a SKU.
          if (c.cprops && c.cprops.fc && fc) {
            const cleanCfc = String(c.cprops.fc).replace(/^-/, "").toUpperCase();
            const cleanFc = String(fc).replace(/^-/, "").toUpperCase();
            return `-${cleanFc}-`.includes(`-${cleanCfc}-`) || `-${cleanCfc}-`.includes(`-${cleanFc}-`);
          }

          // 3. Fallback: If one is missing finish metadata, match on ID only.
          return true;
        })
      );
      el.classList.toggle("wishlisted", isWishlisted);
      if (img) img.src = isWishlisted ? WISHLISTED_ICON : DEFAULT_ICON;
    });
  },

  showToast(message) {
    let toast = document.getElementById("swym-custom-toast");
    if (!toast) {
      toast = document.createElement("div");
      toast.id = "swym-custom-toast";
      toast.style.cssText = [
        "position:fixed", "bottom:24px", "left:50%", "transform:translateX(-50%)",
        "background:#222", "color:#fff", "padding:10px 22px", "border-radius:6px",
        "font-size:14px", "z-index:99999", "pointer-events:none",
        "opacity:0", "transition:opacity 0.3s ease"
      ].join(";");
      document.body.appendChild(toast);
    }
    toast.textContent = message || "Item Saved";
    toast.style.opacity = "1";
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(() => { toast.style.opacity = "0"; }, 2500);
  },
};
window.Wishlist = Wishlist;


/* ============================================================================
 * 22. PCC (Powdercoat Colour) TOGGLE
 * ----------------------------------------------------------------------------
 * Clicking the PCC tile flips an alternate finish flow — resets any picked
 * finish and reveals the colour wrapper WITHOUT pre-selecting a colour; the
 * shopper's own swatch click drives updateSelections from there.
 * ========================================================================= */

const PccFlow = {
  bind() {
    if (!DOM.pccVariant) return;

    DOM.pccVariant.addEventListener("click", () => {
      DOM.pccVariant.classList.toggle("selected");
      DOM.pccWrapper.classList.toggle("d-none");

      // Reset every finish radio back to unchecked.
      document.querySelectorAll('[name="finish"]').forEach((input) => {
        input.checked = false;
      });

      // ✅ FIX: this used to grab `document.querySelector(".with_pcc")` —
      // literally the FIRST powdercoat colour swatch anywhere in the DOM —
      // and check it automatically as soon as the tile opened (e.g. "APM"
      // got silently pre-selected the instant the shopper clicked "Powder
      // Coat", before they'd chosen a colour).
      //
      // No swatch should be pre-checked here. But we still need the rest of
      // the app (cart button disable, placeholder product image, etc.) to
      // notice that "finish" just became unselected, and filterOptions()
      // needs a recompute pass to see the tile's new open/closed state (see
      // the pccManuallyOpen fix in SkuCandidates.filterOptions). So instead
      // of faking a finish selection, we re-dispatch change on the last
      // section that's still genuinely checked (Backplate/Function);
      // updateSelections then walks forward, finds the (now-empty) finish
      // section unselected, and correctly disables the cart button / shows
      // the placeholder — without ever claiming a colour was picked.
      let lastCheckedInput = null;
      DOM.customVariants.forEach((section) => {
        const checked = section.querySelector("input:checked");
        if (checked) lastCheckedInput = checked;
      });
      if (lastCheckedInput) {
        lastCheckedInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
      // updateSelections() only calls AddonDropdowns.updateVisibility() via
      // resolveSkuProduct(), which won't run while finish is still unselected
      // (the state we're deliberately in right after opening the tile) — so
      // call it directly here too, on both open and close.
      AddonDropdowns.updateVisibility();
    });
  },
};


/* ============================================================================
 * 23. BOOTSTRAP
 * ----------------------------------------------------------------------------
 * Page-load entry point. Wires up listeners, applies URL prefill, checks
 * initial wishlist state.
 * ========================================================================= */

function bootstrapConfigurator() {
  // Cover the MAIN product's default image with a skeleton placeholder as
  // early as possible when the URL targets a specific SKU, so the shopper
  // never sees the wrong image while fromSku()/resolveSkuProduct() resolve.
  Gallery.showInitialSkeleton();

  // Disable all specbook/wishlist controls immediately on page load.
  // They will be re-enabled by syncSpecbookButtonState(true) only after
  // resolveSkuProduct() confirms a real product exists for the chosen SKU.
  syncSpecbookButtonState(false);
  if (DOM.optionAccordion) {
    SkuCandidates.captureFirstPrefix();

    // Listen for option-radio changes across all variant sections.
    DOM.customVariants.forEach((section) => {
      section.querySelectorAll("input").forEach((input) => {
        input.addEventListener("change", updateSelections);
      });
    });

    document.querySelectorAll(".form-select-dropdown-product, .entrance-dropdown-product").forEach((dd) => {
      dd.addEventListener("change", () => {
        AddonDropdowns.updateFormDropdownProperty();
      });
    });

    CartButton.bindClick();
    PccFlow.bind();

    if (DOM.customVariants.length === 1) {
      SkuCandidates.filterOptions(DOM.customVariants[0], {});
    }

    // ✅ NEW: hide inner content of sections 2+ when no SKU in URL START
    if (DOM.customVariants.length > 1) {
      const urlParams = Utils.getSearchParams();
      const hasSku = urlParams.has("sku") && urlParams.get("sku") !== "";
      if (!hasSku) {
        for (let j = 1; j < DOM.customVariants.length; j++) {
          DOM.customVariants[j].querySelectorAll("input").forEach((opt) => {
            opt.parentElement.style.display = "none";
            opt.checked = false;
          });
          const pccTile = DOM.customVariants[j].querySelector(".pcc_variant");
          if (pccTile) {
            pccTile.style.display = "none";
            pccTile.classList.remove("selected");
            DOM.pccWrapper?.classList.add("d-none");
          }
        }
      }
    }
    // ✅ NEW: hide inner content of sections 2+ when no SKU in URL END

    UrlPrefill.fromSku();
    UrlPrefill.applyFirstVariant();
    // After fetchProducts resolves, UrlPrefill.applyRestAfterFetch() is the
    // mechanism for replaying URL-based variants on second+ option. It's called
    // from inside SkuCandidates.fetch once State is populated.
  }

  // Wishlist icon state for the Main Product spec-book buttons.
  document.querySelectorAll(".js_specbook_button").forEach((btn) => {
    const empi = btn.getAttribute("data-empi");
    const epi = btn.getAttribute("data-epi");
    const du = btn.getAttribute("data-du");
    Wishlist.refreshButtonState(epi, empi, du);
  });

  Wishlist.bindModal();
  Wishlist.bindCreate();
  Specbook.bindDownload();
}

bootstrapConfigurator();

/**
 * syncSpecbookButtonState — single source of truth for enabling or disabling
 * the wishlist heart icon (.js_specbook_button) and the two specbook action
 * buttons (.js_specbook_button[button], .js_specbook_download_button).
 *
 * valid === true  : a real Shopify product was found for the current SKU
 *                   combination → enable all controls.
 * valid === false : no SKU selected, invalid combination, URL edit leading
 *                   to a non-existent product, or an API error.
 *                   → disable all controls.
 *
 * Called from:
 *   • resolveSkuProduct()         — after every successful or failed fetch
 *   • updateSelections()          — when the selection is incomplete
 *   • bootstrapConfigurator()     — page-load default (disable)
 *   • popstate listener below     — whenever the URL changes
 */
function syncSpecbookButtonState(valid) {
  const isProductPage = window.location.pathname.includes("/products/");
  // Outside of product pages we never need to manage these buttons.
  if (!isProductPage) return;

  const buttons = document.querySelectorAll(
    ".js_specbook_button, .js_specbook_download_button"
  );

  buttons.forEach((el) => {
    if (valid) {
      // Enable
      el.disabled = false;
      el.removeAttribute("disabled");
      el.style.pointerEvents = "";
      el.style.opacity = "";
    } else {
      // Disable
      el.disabled = true;
      el.setAttribute("disabled", "disabled");
      el.style.pointerEvents = "none";
      el.style.opacity = "0.5";
    }
  });
}

// Re-evaluate button state whenever the browser URL changes (back/forward
// navigation or the user manually edits the address bar).
window.addEventListener("popstate", () => {
  // If State.activeSkuProduct is still set from a prior navigation within the
  // same page session AND the URL still has a matching ?sku= param, keep
  // buttons enabled; otherwise disable immediately while we wait for
  // resolveSkuProduct to run (it will re-enable if valid).
  const urlParams = Utils.getSearchParams();
  const hasSku = urlParams.has("sku") && urlParams.get("sku") !== "";
  if (!hasSku) {
    State.activeSkuProduct = null;
    syncSpecbookButtonState(false);
  }
});

// ✅ Listen for dynamically hydrated function list, to parse functions/finishes
// that are outside the initial Liquid-rendered set (e.g. 0-E).
document.addEventListener("function-list-hydrated", () => {
  console.log("[UrlPrefill] function list hydrated, re-running prefill");

  // Re-bind change event listener to any newly hydrated inputs
  if (DOM.customVariants) {
    DOM.customVariants.forEach((section) => {
      section.querySelectorAll("input").forEach((input) => {
        input.removeEventListener("change", updateSelections);
        input.addEventListener("change", updateSelections);
      });
    });
  }

  // Only re-run URL prefill if it hasn't been applied yet (i.e. the user
  // is still on the initial page load path). If urlPrefillApplied is already
  // true the user may have made manual selections after the initial prefill
  // (e.g. changed the backplate from PS6 to PS10) — re-running fromSku()
  // here would re-parse the original ?sku= URL, overwrite _params with the
  // old backplate code, and applyRestAfterFetch() would then re-select the
  // original options, overriding the user's manual choices and causing
  // filterOptions to show function counts for the wrong backplate.
  if (!State.urlPrefillApplied) {
    UrlPrefill.fromSku();
    UrlPrefill.applyRestAfterFetch();
  }

  // Re-run filterOptions for visible sections so newly hydrated options are displayed
  if (DOM.customVariants) {
    DOM.customVariants.forEach((section, i) => {
      if (i === 0) {
        if (DOM.customVariants.length === 1) {
          SkuCandidates.filterOptions(section, {}, "function-list-hydrated(single)");
        }
        return;
      }
      if (section.style.display !== "none") {
        // Build precedingValues using ONLY selections from sections that appear
        // BEFORE index i in DOM order — same fix as backgroundFetch.finally.
        // The old approach included selections from LATER sections (e.g. finish)
        // which over-constrained the function list.
        const getSectionKey = (sec) => sec?.querySelector("input")?.name || sec?.getAttribute("data-name");
        const precedingValues = {};
        for (let j = 0; j < i; j++) {
          const prevKey = getSectionKey(DOM.customVariants[j]);
          if (prevKey && State.selectedValues[prevKey] !== undefined) {
            precedingValues[prevKey] = State.selectedValues[prevKey];
          }
        }
        SkuCandidates.filterOptions(section, precedingValues, "function-list-hydrated");
      }
    });
  }
});


/* ============================================================================
 * 18. CART ADD-ON PRODUCT PRICES
 * ========================================================================= */

const CartAddonPrices = {
  async init() {
    const rows = document.querySelectorAll(".cart-items tbody tr.cart-item");
    if (!rows.length) return;

    let grandTotal = 0;
    let detectedCurrency = null;

    for (const row of rows) {
      const priceEl = row.querySelector(".js-cart-item-price");
      const qtyInput = row.querySelector('input[name="updates[]"], input.quantity__input');
      const itemQty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      const lineIndex = priceEl?.getAttribute("data-line-index");

      const isZeroPriceAddon = priceEl && ["0", "0.00", "0.0"].includes(priceEl.getAttribute("data-price"));

      if (isZeroPriceAddon) {
        const handle = priceEl.getAttribute("data-handle");
        const dcode = priceEl.getAttribute("data-dcode");
        const finish = priceEl.getAttribute("data-finish") || "";

        const resolved = await this.resolveAddonPrice({ handle, dcode, finish });
        if (resolved && resolved.unitPrice > 0) {
          detectedCurrency = resolved.currency || detectedCurrency;
          const symbol = getCurrencySymbol(resolved.currency);
          const unitPriceStr = `${symbol}${resolved.unitPrice.toFixed(2)}`;
          const unitPriceFull = `${unitPriceStr} ${resolved.currency || ""}`.trim();
          const lineTotal = resolved.price * itemQty;
          const lineTotalStr = `${symbol}${lineTotal.toFixed(2)}`;

          // Update unit price in cart table (display only — this is a
          // "shown value" for a genuinely free add-on, not what's charged)
          priceEl.textContent = unitPriceFull;

          // Update line total in cart table (display only, see above)
          if (lineIndex) {
            const totalEls = document.querySelectorAll(`.js-cart-item-total[data-line-index="${lineIndex}"]`);
            totalEls.forEach((totalEl) => {
              totalEl.textContent = `${lineTotalStr} ${resolved.currency || ""}`.trim();
            });
          }

          // NOTE: intentionally NOT adding `lineTotal` to grandTotal here.
          // This add-on is actually priced at $0 (checkout charges $0 for
          // it), so the cart's total must stay in sync with checkout even
          // though we're displaying a non-zero "value" price on the line.
        }
      } else {
        // Standard item - extract line total or unit price * qty
        const totalEl = row.querySelector(".js-cart-item-total");
        let lineAmount = 0;
        if (totalEl) {
          const text = totalEl.textContent.trim();
          const match = text.match(/[\d,]+\.?\d*/);
          if (match) lineAmount = parseFloat(match[0].replace(/,/g, "")) || 0;
        }
        if (lineAmount <= 0 && priceEl) {
          const text = priceEl.textContent.trim();
          const match = text.match(/[\d,]+\.?\d*/);
          if (match) {
            const unitAmt = parseFloat(match[0].replace(/,/g, "")) || 0;
            lineAmount = unitAmt * itemQty;
          }
        }
        grandTotal += lineAmount;
      }
    }

    // Recalculate and update cart totals (subtotal, GST, estimated total)
    this.updateCartTotals(grandTotal, detectedCurrency);
  },

  updateCartTotals(grandTotal, currency) {
    if (grandTotal <= 0) return;

    const calcWrapper = document.querySelector(".cart_price_calculation");
    const isAU = calcWrapper?.getAttribute("data-is-au") === "true";

    // Extract currency from DOM if not passed
    const totalEl = document.querySelector(".js-cart-total");
    let symbol = "$";
    let currencyCode = currency || "";
    if (totalEl) {
      const text = totalEl.textContent.trim();
      const codeMatch = text.match(/[A-Z]{3}/);
      if (codeMatch) currencyCode = codeMatch[0];
      symbol = getCurrencySymbol(currencyCode);
    }

    const grandTotalStr = `${symbol}${grandTotal.toFixed(2)} ${currencyCode}`.trim();
    if (totalEl) totalEl.textContent = grandTotalStr;

    const subtotalEl = document.querySelector(".js-cart-subtotal");
    const gstEl = document.querySelector(".js-cart-gst");

    if (isAU) {
      const gst = grandTotal * 0.10;
      const subtotal = grandTotal - gst;
      if (gstEl) gstEl.textContent = `${symbol}${gst.toFixed(2)} ${currencyCode}`.trim();
      if (subtotalEl) subtotalEl.textContent = `${symbol}${subtotal.toFixed(2)} ${currencyCode}`.trim();
    } else if (subtotalEl) {
      subtotalEl.textContent = grandTotalStr;
    }
  },

  async resolveAddonPrice({ handle, dcode, finish }) {
    let baseCode = dcode;
    let bundleComponentQty = null;
    let bundleComponentPrice = null;
    let bundleComponentCurrency = null;

    if (!baseCode && handle) {
      try {
        const data = await gql(Queries.bundleComponentProduct, { handle });
        const component = data?.product?.selectedOrFirstAvailableVariant?.components?.nodes?.[0];
        const componentTitle = component?.productVariant?.product?.title;
        if (componentTitle) baseCode = componentTitle.trim();
        if (Number(component?.quantity) > 0) bundleComponentQty = Number(component.quantity);
        const componentPriceAmount = component?.productVariant?.price?.amount;
        if (componentPriceAmount != null) {
          bundleComponentPrice = parseFloat(componentPriceAmount);
          bundleComponentCurrency = component?.productVariant?.price?.currencyCode || null;
        }
      } catch (e) {
        console.error("[CartAddonPrices] component lookup failed:", e);
      }
    }

    if (!baseCode) return null;

    let finishCode = finish ? String(finish).trim() : "";
    if (finishCode && !finishCode.startsWith("-")) {
      const finishInput = document.querySelector(`input[name="finish"][title="${finishCode}"]`);
      if (finishInput) finishCode = finishInput.value;
    }

    const fetchProductByHandle = async (h) => {
      try {
        const data = await gql(Queries.resolvedSkuProduct, { handle: h });
        return data?.product || null;
      } catch (e) {
        return null;
      }
    };

    let resolvedHandle = finishCode ? `${baseCode}${finishCode}` : "";
    let product = resolvedHandle ? await fetchProductByHandle(resolvedHandle) : null;

    if (!product) {
      // Fallback: PSS→PC, SSS→SN, else→SS
      resolvedHandle = `${baseCode}${sparePartFallbackFinish(finishCode)}`;
      product = await fetchProductByHandle(resolvedHandle);
    }

    if (!product && bundleComponentPrice !== null) {
      const finalQty = bundleComponentQty !== null ? bundleComponentQty : 1;
      const calcPrice = AddonDropdowns.calculateAddonPrice(bundleComponentPrice, finalQty);
      return { price: calcPrice, unitPrice: bundleComponentPrice, currency: bundleComponentCurrency };
    }

    if (!product) return null;

    const unitPrice = parseFloat(product.priceRange?.minVariantPrice?.amount || 0);
    const currency = product.priceRange?.minVariantPrice?.currencyCode || null;
    const finalQty = bundleComponentQty !== null ? bundleComponentQty : 1;
    const calcPrice = AddonDropdowns.calculateAddonPrice(unitPrice, finalQty);

    return { price: calcPrice, unitPrice, currency };
  }
};

const runCartAddonPrices = () => CartAddonPrices.init();

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", runCartAddonPrices);
} else {
  runCartAddonPrices();
}

// Observe DOM mutations on cart items container so prices update whenever quantity changes / section re-renders
const cartItemsContainer = document.getElementById("main-cart-items") || document.querySelector("cart-items");
if (cartItemsContainer) {
  const observer = new MutationObserver(() => runCartAddonPrices());
  observer.observe(cartItemsContainer, { childList: true, subtree: true });
}

// Also subscribe to Dawn pubsub cart updates if defined
if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => runCartAddonPrices());
}